import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Table, Tag, Input, InputNumber, Button, Space, Pagination, Modal, Typography, message, Switch, Checkbox } from 'antd';
import request from '../../../../utils/request';

const { Text } = Typography;

type PickStepRendererProps = {
  step: any;
  data: any;
  metrics: Record<string, any>;
  onChange: (key: string, val: any) => void;
  onSubmit: () => void;
  instructionCodeFromParent?: string;
  canOperate?: boolean;
  taskId?: number | null;
  stageNo?: number | null;
  executionSessionId?: string;
  draftApiBase?: string;
  initialPickLogs?: Array<{
    id: string;
    container_code: string;
    target_code: string;
    sorting_mark?: string;
    qty: number;
    operated_at: string;
    off_shelf?: boolean;
  }>;
};

const PickStepRenderer: React.FC<PickStepRendererProps> = ({ step, data, metrics, onChange, onSubmit, instructionCodeFromParent, canOperate, taskId, stageNo, executionSessionId, draftApiBase, initialPickLogs }) => {
  const rows = Array.isArray(data?.pick_rows) ? data.pick_rows : [];
  const isReadonly = canOperate === false; // 阶段已完成/已跳过：仅允许只读查看（以 Execute 标题行同口径为准）
  const base = String(draftApiBase || '/api/v1/instruction/exec/tasks').replace(/\/+$/, '');
  // 左右独立分页
  const [leftPage, setLeftPage] = useState<number>(1);
  const [leftPageSize, setLeftPageSize] = useState<number>(20);
  const [hideCompleted, setHideCompleted] = useState<boolean>(true); // 仅显示未完成记录，默认打开
  const [rightPage, setRightPage] = useState<number>(1);
  const [rightPageSize, setRightPageSize] = useState<number>(20);
  // 统一的垂直分割线样式
  const vSepStyle: React.CSSProperties = useMemo(() => ({
    display: 'inline-block',
    verticalAlign: 'middle',
    margin: '0 12px',
    height: 16,
    borderLeft: '2px solid #8c8c8c'
  }), []);
  // 拣货记录（需在任何使用它的 useMemo 之前声明）
  const [pickLogs, setPickLogs] = useState<Array<{
    id: string;
    container_code: string;
    target_code: string;
    sorting_mark?: string;
    qty: number;
    operated_at: string;
    off_shelf?: boolean;
  }>>(() => (Array.isArray(initialPickLogs) ? initialPickLogs : []));

  // 外部初始拣货记录（仅当本地为空时）
  useEffect(() => {
    if ((pickLogs || []).length > 0) return;
    if (Array.isArray(initialPickLogs) && initialPickLogs.length > 0) {
      setPickLogs(initialPickLogs);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPickLogs]);

  // 将拣货记录暴露给上层（用于提交阶段时汇总与提交）
  React.useEffect(() => {
    try {
      (window as any).__PICK_LOGS__ = Array.isArray(pickLogs) ? pickLogs : [];
    } catch {}
  }, [pickLogs]);

  // 后端草稿：加载（仅当本地为空时）
  useEffect(() => {
    (async () => {
      try {
        if (!taskId) return;
        if ((pickLogs || []).length > 0) return; // 本地已经有数据则不覆盖
        const resp = await request.get(`${base}/${taskId}/picking-draft/`);
        const draft = resp?.data?.draft || {};
        const dStage = Number.isFinite(Number(draft?.stage_idx)) ? Number(draft.stage_idx) : null;
        const curStage = Number.isFinite(Number(stageNo)) ? Number(stageNo) : null;
        if (curStage != null && dStage != null && curStage !== dStage) {
          // 不同阶段草稿，不自动加载
          return;
        }
        const logs = Array.isArray(draft?.pick_logs) ? draft.pick_logs : [];
        if (logs.length > 0) setPickLogs(logs);
      } catch {}
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]); 

  // 后端草稿：保存（debounce）
  const saveTimerRef = useRef<any>(null);
  useEffect(() => {
    if (!taskId) return;
    if (isReadonly) return;
    try { if (!Array.isArray(pickLogs)) return; } catch { return; }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        await request.post(`${base}/${taskId}/picking-draft/`, {
          stage_idx: (Number.isFinite(Number(stageNo)) ? Number(stageNo) : null),
          pick_logs: pickLogs,
          stats: {
            total_qty: (pickLogs || []).reduce((s, r)=> s + (Number(r?.qty||0) || 0), 0),
            kinds: Array.from(new Set((pickLogs || []).map(r=> String(r?.target_code || '').trim()).filter(Boolean))).length,
            containers: Array.from(new Set((pickLogs || []).map(r=> String(r?.container_code || '').trim()).filter(Boolean))).length,
            target_total: (rows || []).reduce((s: number, r: any) => s + (Number(r?.target_qty || 0) || 0), 0),
            picked_total: (pickLogs || []).reduce((s, r)=> s + (Number(r?.qty||0) || 0), 0),
            target_scope: 'inventory_unit',
            execution_session_id: String(executionSessionId || '').trim() || undefined,
          }
        });
      } catch {}
    }, 1000);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [pickLogs, taskId, stageNo, rows, executionSessionId]);

  useEffect(() => {
    // 左表分页保护
    const totalLeft = rows.length;
    const maxLeft = Math.max(1, Math.ceil(totalLeft / leftPageSize));
    if (leftPage > maxLeft) setLeftPage(1);
    // 右表分页保护
    const totalRight = rows.length;
    const maxRight = Math.max(1, Math.ceil(totalRight / rightPageSize));
    if (rightPage > maxRight) setRightPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length, leftPageSize, rightPageSize]);

  const leftPagedRows = useMemo(() => {
    const start = (leftPage - 1) * leftPageSize;
    return rows.slice(start, start + leftPageSize);
  }, [rows, leftPage, leftPageSize]);

  // 基于拣货记录动态修正左侧列表的已拣/差额
  const pickedByTarget = useMemo(() => {
    const map: Record<string, number> = {};
    try {
      (pickLogs || []).forEach((rec) => {
        const u = String(rec?.target_code || '').trim();
        const q = Number(rec?.qty || 0);
        if (!u || !(q > 0)) return;
        map[u] = (map[u] || 0) + q;
      });
    } catch {}
    return map;
  }, [pickLogs]);

  const leftAdjustedRows = useMemo(() => {
    return rows.map((r: any) => {
      const key = String(r?.target_label ?? '').trim();
      const targetQty = Number(r?.target_qty ?? 0);
      const picked = Number(pickedByTarget[key] || 0);
      // 差额数量允许为负：= 目标数量 - 已拣数量
      const diff = targetQty - picked;
      return { ...r, picked_qty: picked, diff_qty: diff };
    });
  }, [rows, pickedByTarget]);

  // 排序：未完成在前，已完成（diff<=0）在后；同组内按目标数量 target_qty 从大到小
  const leftSortedAdjustedRows = useMemo(() => {
    const arr = [...leftAdjustedRows];
    arr.sort((a: any, b: any) => {
      const aDone = Number(a?.diff_qty ?? 0) <= 0;
      const bDone = Number(b?.diff_qty ?? 0) <= 0;
      if (aDone !== bDone) return aDone ? 1 : -1; // 未完成优先
      const aQty = Number(a?.target_qty ?? 0);
      const bQty = Number(b?.target_qty ?? 0);
      return bQty - aQty; // 目标数量降序
    });
    return arr;
  }, [leftAdjustedRows]);

  const leftFilteredRows = useMemo(() => {
    if (!hideCompleted) return leftSortedAdjustedRows;
    return leftSortedAdjustedRows.filter((r: any) => Number(r?.diff_qty ?? 0) > 0);
  }, [hideCompleted, leftSortedAdjustedRows]);

  const leftPagedAdjustedRows = useMemo(() => {
    const start = (leftPage - 1) * leftPageSize;
    return leftFilteredRows.slice(start, start + leftPageSize);
  }, [leftFilteredRows, leftPage, leftPageSize]);

  useEffect(() => {
    const totalLeft = leftFilteredRows.length;
    const maxLeft = Math.max(1, Math.ceil(totalLeft / leftPageSize));
    if (leftPage > maxLeft) setLeftPage(1);
  }, [leftFilteredRows.length, leftPageSize, leftPage]);

  // 全部目标已完成提示（拣货目标达成）
  const [allDonePrompted, setAllDonePrompted] = useState<boolean>(false);
  useEffect(() => {
    try {
      const rowsAll = leftAdjustedRows || [];
      const hasTargets = rowsAll.length > 0;
      const allDone = hasTargets && rowsAll.every((r: any) => Number(r?.diff_qty ?? 0) <= 0);
      if (allDone && !allDonePrompted) {
        Modal.success({
          title: '拣货目标已达成',
          content: '请核对后提交拣货记录',
          okText: '知道了'
        });
        setAllDonePrompted(true);
      } else if (!allDone && allDonePrompted) {
        // 目标未全部达成，重置提示标志，便于再次达成时重新提示
        setAllDonePrompted(false);
      }
    } catch {}
  }, [leftAdjustedRows, allDonePrompted]);



  // 右侧分页的切片将在 rightRows 定义之后计算

  // 统计（后端未提供时，本地推导）
  const derivedStats = useMemo(() => {
    const locationSet = new Set<string>();
    const containerAll = new Set<string>();
    const containerPicked = new Set<string>();
    let targetTotal = 0;
    let pickedTotal = 0;
    let diffTotal = 0;
    rows.forEach((r:any) => {
      const loc = String(r?.location_code ?? '').trim();
      if (loc) locationSet.add(loc);
      const cont = String(r?.container_code ?? '').trim();
      if (cont) {
        containerAll.add(cont);
        // 是否计入“有拣出过的容器”：按左侧调整后的 picked_qty 判断
        const adj = leftAdjustedRows.find((x:any) => String(x?.target_label ?? '') === String(r?.target_label ?? '') && String(x?.target_content ?? '') === String(r?.target_content ?? ''));
        const pickedForThis = Number((adj && adj.picked_qty) ?? r?.picked_qty ?? 0);
        if (pickedForThis > 0) containerPicked.add(cont);
      }
      const adjTarget = Number(r?.target_qty ?? 0);
      const adjPicked = Number((leftAdjustedRows.find((x:any) => String(x?.target_label ?? '') === String(r?.target_label ?? '') && String(x?.target_content ?? '') === String(r?.target_content ?? ''))?.picked_qty) ?? r?.picked_qty ?? 0);
      const adjDiff = Math.max(adjTarget - adjPicked, 0);
      targetTotal += adjTarget;
      pickedTotal += adjPicked;
      diffTotal += adjDiff;
    });
    return {
      location_count: locationSet.size,
      target_container_total: containerAll.size,
      picked_container_count: containerPicked.size,
      target_total: targetTotal,
      picked_total: pickedTotal,
      diff_total: diffTotal,
    };
  }, [rows, leftAdjustedRows]);
  const stats = data?.stats || derivedStats;
  const boardRate = useMemo(() => {
    const targetTotal = Number(stats?.target_total ?? 0);
    const pickedTotal = Number(stats?.picked_total ?? 0);
    const rateValue = targetTotal > 0 ? (pickedTotal / targetTotal) * 100 : null;
    const rateText = (rateValue == null || !Number.isFinite(rateValue)) ? '-' : `${Number(rateValue.toFixed(1))}%`;
    return {
      rateValue: (rateValue == null || !Number.isFinite(rateValue)) ? null : Number(rateValue),
      rateText,
    };
  }, [stats]);
  // 左侧标题右侧统计：目标拣货总数/已拣总数/差额数 ｜ 目标物种类总数/已完成拣货目标物种类总数/差额数
  const leftHeaderRight = useMemo(() => {
    const targetTotal = Number(stats?.target_total ?? 0);
    const pickedTotal = Number(stats?.picked_total ?? 0);
    // 目标差额：仅累计正差额（逐行累加大于0的 diff_qty）
    const diffTotal = leftAdjustedRows.reduce((sum: number, r: any) => {
      const d = Number(r?.diff_qty ?? 0);
      return sum + (d > 0 ? d : 0);
    }, 0);
    // 以 target_label + target_content 作为目标物种类键，基于调整后的行聚合
    const groupMap: Record<string, { target: number; picked: number }> = {};
    leftAdjustedRows.forEach((r: any) => {
      const key = `${String(r?.target_label ?? '')}|${String(r?.target_content ?? '')}`;
      if (!groupMap[key]) groupMap[key] = { target: 0, picked: 0 };
      groupMap[key].target += Number(r?.target_qty ?? 0);
      groupMap[key].picked += Number(r?.picked_qty ?? 0);
    });
    const kindTotal = Object.keys(groupMap).length;
    let kindDone = 0;
    Object.values(groupMap).forEach(g => {
      if (g.picked >= g.target) kindDone += 1;
    });
    const kindDiff = Math.max(kindTotal - kindDone, 0);
    const overAmount = leftAdjustedRows.reduce((s:number, r:any) => {
      const d = Number(r?.diff_qty ?? 0);
      return s + (d < 0 ? Math.abs(d) : 0);
    }, 0);
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        <span>目标 {targetTotal}/{pickedTotal}/差额{diffTotal}</span>
        <span style={vSepStyle} />
        <span>目标物种类 {kindTotal}/{kindDone}/差额{kindDiff}</span>
        {overAmount > 0 ? (<><span style={vSepStyle} /><span style={{ color:'#cf1322', fontWeight: 700 }}>超额拣货 {overAmount}</span></>) : null}
      </span>
    );
  }, [leftAdjustedRows, stats, vSepStyle]);

  // 右侧标题右侧统计：放在 rightRows 定义之后

  // 左右分区列定义
  const renderSortingMarkLeft = (r: any) => {
    // 优先使用后端若已提供到左行的标识字段
    const pick = (v: any) => (Array.isArray(v) ? v.filter(Boolean).join(' / ') : (v || ''));
    const direct =
      pick(r?.sorting_mark) ||
      pick(r?.sorting_codes) ||
      pick(r?.sorting_code) ||
      pick(r?.target_sorting_codes);
    if (direct) return direct;
    // 若存在 unit_type 为 LIU 的散货单元，尝试从目标物内容字段中解析（如后端已将 codes 展平）
    // 兼容字段：r.unit_type === 'LIU' 且 r.product_sorting_codes / r.product_sorting_code
    if (String(r?.unit_type || '').toUpperCase() === 'LIU') {
      const liuMark = pick(r?.product_sorting_codes) || pick(r?.product_sorting_code);
      if (liuMark) return liuMark;
    }
    // 兜底
    return '-';
  };

  const leftColumns: any[] = [
    { title: '目标物', dataIndex: 'target_label', width: 180, render: (v: any) => (
      <Text copyable={{ text: String(v ?? '') }} style={{ fontFamily: 'monospace', color: '#595959' }}>
        {v}
      </Text>
    ) },
    { title: '分拣标识', dataIndex: 'sorting_mark', width: 220, render: (_: any, r: any) => renderSortingMarkLeft(r) },
    { title: '目标数量', dataIndex: 'target_qty', width: 100 },
    { title: '已拣数量', dataIndex: 'picked_qty', width: 100, render: (v:any) => Number(v ?? 0) },
    { title: '差额数量', dataIndex: 'diff_qty', width: 100, render: (v:any) => {
      const num = Number(v ?? 0);
      const style = num < 0 ? { fontWeight: 700, color: '#cf1322' } : undefined;
      return <span style={style}>{num}</span>;
    }},
    { title: '已完成', dataIndex: 'done', width: 80, render: (_: any, r:any) => {
      const done = Number(r?.diff_qty ?? 0) <= 0; // 差额<=0 表示已完成
      return <Tag color={done ? 'green' : 'default'}>{done ? '是' : '否'}</Tag>;
    } },
  ];
  const rightColumns: any[] = [
    { title: '仓位', dataIndex: 'location_code', width: 120 },
    { title: '容器', dataIndex: 'container_code', width: 140, render: (v: any) => (
      <Text copyable={{ text: String(v ?? '') }} style={{ fontFamily: 'monospace', color: '#595959' }}>
        {v}
      </Text>
    ) },
    { 
      title: '是否单装', 
      dataIndex: 'is_single_pack', 
      width: 100, 
      render: (_: any, r: any) => {
        const code = String(r?.container_code ?? '').trim();
        const items = contentsSummaryMap[code] || [];
        const distinctUnits = new Set(
          items.map((it: any) => String(it?.unit_code || '').trim()).filter(Boolean)
        ).size;
        const isSingle = items.length > 0 && distinctUnits === 1;
        // 样式：单装浅绿，混装浅蓝
        const style: React.CSSProperties = isSingle
          ? { backgroundColor: '#f6ffed', color: '#135200', borderColor: 'transparent' }
          : { backgroundColor: '#e6f4ff', color: '#0958d9', borderColor: 'transparent' };
        return <Tag style={{ ...style, padding: '0 8px' }}>{isSingle ? '单装' : '混装'}</Tag>;
      } 
    },
    {
      title: '容器内数量 | 已拣出',
      dataIndex: 'container_qty_and_picked',
      width: 160,
      render: (_: any, r: any) => {
        const code = String(r?.container_code ?? '').trim();
        const items = contentsSummaryMap[code] || [];
        const total = items.reduce((sum: number, it: any) => sum + Number(it?.qty ?? 0), 0);
        const picked = Number(r?.picked_out_sum_eff ?? r?.picked_out_sum ?? 0);
        if (!code) return '-';
        return (
          <a onClick={() => openInOutModal(code)} title="查看容器内容与已拣出明细">
            {(total || 0)} <span style={{ color: '#8c8c8c' }}>|</span> {picked}
          </a>
        );
      }
    },
    {
      title: '理论余量',
      dataIndex: 'theoretical_remain',
      width: 110,
      render: (_: any, r: any) => {
        const code = String(r?.container_code ?? '').trim();
        if (!code) return '-';
        const totalInContainer = Number(r?.container_qty_eff ?? 0);
        const pickedOutSum = Number(r?.picked_out_sum_eff ?? r?.picked_out_sum ?? 0);
        const remain = totalInContainer - pickedOutSum;
        const style = remain < 0 ? { fontWeight: 700, color: '#cf1322' } : undefined;
        return (
          <a onClick={() => openRemainModal(code, pickedOutSum, totalInContainer)} title="查看理论余量明细">
            <span style={style}>{remain}</span>
          </a>
        );
      }
    },
  ];

  // 选择列（左表第一列）
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedLeftRows, setSelectedLeftRows] = useState<any[]>([]);
  const [debugVisible, setDebugVisible] = useState<boolean>(false);
  const [selectionInitialized, setSelectionInitialized] = useState<boolean>(false);
  const [leftPageSizeInitialized, setLeftPageSizeInitialized] = useState<boolean>(false);

  // 初次打开时，根据记录条数自动设置左侧分页大小，确保第一页显示全部目标
  useEffect(() => {
    if (!leftPageSizeInitialized && rows.length > 0) {
      const desired = Math.max(10, Math.ceil(rows.length / 10) * 10);
      setLeftPageSize(desired);
      setLeftPage(1);
      setLeftPageSizeInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length, leftPageSizeInitialized]);

  // 统一的左表 rowKey 生成方法，供初始化与 Table 共用
  const getLeftRowKey = (r: any) => {
    const key =
      r?.row_id ??
      r?.id ??
      `${String(r?.target_label ?? '')}|${String(r?.target_content ?? '')}|${String(r?.location_code ?? '')}|${String(r?.container_code ?? '')}`;
    return String(key);
  };

  // 初次进入时默认“全选”左侧所有目标物
  useEffect(() => {
    if (!selectionInitialized && rows.length > 0) {
      const allKeys = rows.map(getLeftRowKey);
      setSelectedRowKeys(allKeys);
      setSelectedLeftRows(rows);
      setSelectionInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length, selectionInitialized]);
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[], selectedRows: any[]) => {
      setSelectedRowKeys(keys);
      setSelectedLeftRows(selectedRows);
    },
    preserveSelectedRowKeys: true,
  };

  // 仅显示未完成时：从选择集中移除已隐藏的已完成记录
  useEffect(() => {
    if (!hideCompleted) return;
    const visibleKeys = new Set(leftFilteredRows.map((r: any) => getLeftRowKey(r)));
    const keptKeys = selectedRowKeys.filter((k) => visibleKeys.has(String(k)));
    const keptRows = selectedLeftRows.filter((r: any) => visibleKeys.has(getLeftRowKey(r)));
    if (keptKeys.length !== selectedRowKeys.length || keptRows.length !== selectedLeftRows.length) {
      setSelectedRowKeys(keptKeys);
      setSelectedLeftRows(keptRows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideCompleted, leftFilteredRows, selectedRowKeys, selectedLeftRows]);

  // 目标键（后端无稳定 target_key 时的前端兜底）
  const getTargetKey = (r: any) => `${String(r?.target_label ?? '')}|${String(r?.target_content ?? '')}`;

  // 基于左侧选择集，计算选中的目标物 key 集合（为空则视为全选）
  const selectedTargetKeySet = useMemo(() => {
    const s = new Set<string>();
    selectedLeftRows.forEach((r: any) => s.add(getTargetKey(r)));
    return s;
  }, [selectedLeftRows]);

  // 实时“拣货差额”映射：基于 leftAdjustedRows（包含 pickLogs 影响）且受左侧选择集过滤
  const adjustedNeedByTarget = useMemo(() => {
    const map: Record<string, number> = {};
    const filterBySelection = selectedTargetKeySet && selectedTargetKeySet.size > 0;
    leftAdjustedRows.forEach((r: any) => {
      if (filterBySelection) {
        if (!selectedTargetKeySet.has(getTargetKey(r))) return;
      }
      const label = String(r?.target_label ?? '').trim();
      if (!label) return;
      const d = Number(r?.diff_qty ?? 0);
      // 只累计正差额
      map[label] = (map[label] || 0) + (d > 0 ? d : 0);
    });
    return map;
  }, [leftAdjustedRows, selectedTargetKeySet]);

  // 右侧列表数据：基于选择集过滤 rows，然后按“容器-仓位”聚合，输出一行一容器-仓位
  const rightRows = useMemo(() => {
    // 1) 过滤
    const filtered = rows.filter((r: any) => selectedTargetKeySet.has(getTargetKey(r)));
    // 2) 聚合：以 container_code + location_code 作为分组键（IOC 与 SC 编码前缀不同，冲突概率低）
    const map = new Map<string, { container_code: string; location_code: string; container_qty: number | undefined; is_single_pack: boolean; picked_out_sum: number }>();
    filtered.forEach((r: any) => {
      const container = String(r?.container_code ?? '');
      const location = String(r?.location_code ?? '');
      const key = `${container}|${location}`;
      const picked = Number(r?.picked_qty ?? 0);
      const qtyIn = r?.container_qty != null ? Number(r.container_qty) : undefined;
      const single = !!r?.is_single_pack;
      if (!map.has(key)) {
        map.set(key, {
          container_code: container,
          location_code: location || '未上架',
          container_qty: qtyIn,
          is_single_pack: single,
          picked_out_sum: picked,
        });
      } else {
        const agg = map.get(key)!;
        agg.picked_out_sum += picked;
        // is_single_pack：只要有一个为真则为真
        agg.is_single_pack = agg.is_single_pack || single;
        // container_qty 取第一次出现的为代表（避免多目标重复累加导致夸大）
      }
    });

    // 调试输出（可开关）
    try {
      // 仅在 debugVisible 时输出，避免噪音
      if (debugVisible) {
        const allKeysSet = new Set<string>();
        rows.forEach((r: any) => allKeysSet.add(getTargetKey(r)));
        // 采样输出，避免过大
        const sampleSelectedKeys = Array.from(selectedTargetKeySet).slice(0, 10);
        const sampleFiltered = filtered.slice(0, 5).map((r: any) => ({
          target: `${String(r?.target_label ?? '')}|${String(r?.target_content ?? '')}`,
          container: String(r?.container_code ?? ''),
          location: String(r?.location_code ?? ''),
          picked: Number(r?.picked_qty ?? 0),
          inQty: r?.container_qty,
        }));
        const sampleRight = Array.from(map.values()).slice(0, 5);
        // eslint-disable-next-line no-console
        console.debug('[PickStepRenderer] debug snapshot', {
          total_rows: rows.length,
          all_target_keys: allKeysSet.size,
          selected_rows_count: selectedLeftRows.length,
          selected_target_keys_count: selectedTargetKeySet.size,
          selected_target_keys_sample: sampleSelectedKeys,
          filtered_count: filtered.length,
          filtered_sample: sampleFiltered,
          right_rows_count: map.size,
          right_rows_sample: sampleRight,
        });
      }
    } catch (e) {
      // ignore debug errors
    }

    // 3) 输出并计算理论余量
    return Array.from(map.values()).map((o) => {
      const remain = o.container_qty != null ? Math.max(o.container_qty - o.picked_out_sum, 0) : undefined;
      return {
        ...o,
        theoretical_remain: remain,
      };
    });
  }, [rows, selectedTargetKeySet, debugVisible, selectedLeftRows.length]);

  // 后端联动：选中目标变化时，调用统一接口获取（在库+产出）容器-仓位列表
  const [backendRightRows, setBackendRightRows] = useState<any[]>([]);
  const [backendLoading, setBackendLoading] = useState<boolean>(false);
  const [backendLoaded, setBackendLoaded] = useState<boolean>(false);
  // 容器内容汇总与明细弹窗
  const [contentsSummaryMap, setContentsSummaryMap] = useState<Record<string, any[]>>({});
  const [contentsLoading, setContentsLoading] = useState<boolean>(false);
  const [contentsModalVisible, setContentsModalVisible] = useState<boolean>(false);
  const [contentsModalContainer, setContentsModalContainer] = useState<string>('');
  const openContentsModal = (code: string) => {
    setContentsModalContainer(code);
    setContentsModalVisible(true);
  };
  // 容器内容+已拣出 合并明细弹窗
  const [inOutModalVisible, setInOutModalVisible] = useState<boolean>(false);
  const [inOutModalContainer, setInOutModalContainer] = useState<string>('');
  const openInOutModal = (code: string) => {
    setInOutModalContainer(String(code || '').trim());
    setInOutModalVisible(true);
  };
  // 理论余量明细弹窗
  const [remainModalVisible, setRemainModalVisible] = useState<boolean>(false);
  const [remainModalContainer, setRemainModalContainer] = useState<string>('');
  const [remainModalPickedOut, setRemainModalPickedOut] = useState<number>(0);
  const [remainModalContainerQty, setRemainModalContainerQty] = useState<number>(0);
  const openRemainModal = (code: string, pickedOutSum: number, containerQty: number) => {
    setRemainModalContainer(code);
    setRemainModalPickedOut(Number(pickedOutSum || 0));
    setRemainModalContainerQty(Number(containerQty || 0));
    setRemainModalVisible(true);
  };
  // 指令系统编码（用于打印标题与标识）由父组件传入，保持与执行页标题一致
  const instructionCode = String(instructionCodeFromParent || '指令#-');
  // 已拣出明细弹窗状态
  const [pickedModalVisible, setPickedModalVisible] = useState<boolean>(false);
  const [pickedModalContainer, setPickedModalContainer] = useState<string>('');
  const openPickedModal = (code: string) => {
    setPickedModalContainer(String(code || '').trim());
    setPickedModalVisible(true);
  };

  // 只读模式切换时，立即清理右侧仓位表相关状态（防止残留旧数据）
  useEffect(() => {
    if (!isReadonly) return;
    try {
      setBackendRightRows([]);
      setBackendLoading(false);
      setBackendLoaded(true);
    } catch {}
    try { setContentsSummaryMap({}); } catch {}
  }, [isReadonly]);
  useEffect(() => {
    // 构造库存单元编码集合：仅使用选中行；若无选中则清空右侧
    if (isReadonly) {
      // 已完成阶段不再展示仓位表（避免读取 DB 当前态导致“容器内=0、已拣>0、余量为负”的误导）
      setBackendRightRows([]);
      setBackendLoading(false);
      setBackendLoaded(true);
      return;
    }
    const source = selectedLeftRows;
    const normalizeCode = (v: any) => String(v ?? '').trim();
    const isAiuRow = (r: any) => {
      const unitType = String(r?.unit_type ?? '').trim().toUpperCase();
      if (unitType === 'AIU') return true;
      const label = normalizeCode(r?.target_label).toUpperCase();
      return label.startsWith('AIU-');
    };
    const aiuCodes = Array.from(
      new Set(
        source
          .filter((r: any) => isAiuRow(r))
          .map((r: any) => normalizeCode(r?.target_label))
          .filter((s: string) => s && s !== '-' && s !== 'null' && s !== 'undefined')
      )
    );
    const nonAiuCodes = Array.from(
      new Set(
        source
          .filter((r: any) => !isAiuRow(r))
          .map((r: any) => normalizeCode(r?.target_label))
          .filter((s: string) => s && s !== '-' && s !== 'null' && s !== 'undefined')
      )
    );
    const codes = Array.from(new Set([...(aiuCodes || []), ...(nonAiuCodes || [])]));
    if (codes.length === 0) {
      setBackendRightRows([]);
      setBackendLoading(false);
      setBackendLoaded(true);
      return;
    }
    setBackendLoading(true);
    const fetchByScope = async (inventoryUnitCodes: string[], containerScope: 'all' | 'storage_only') => {
      if (!Array.isArray(inventoryUnitCodes) || inventoryUnitCodes.length === 0) return [];
      const res = await request.post('/api/v1/storage/containers/by-inventory-units/', {
        inventory_unit_codes: inventoryUnitCodes,
        // 需求：
        // - AIU 目标：显示 storage + IOC（all）
        // - 非 AIU 目标：保持仅 storage（storage_only）
        container_scope: containerScope,
      });
      const payload: any = res?.data ?? {};
      const list = Array.isArray(payload?.results) ? payload.results : [];
      return list;
    };
    Promise.all([
      fetchByScope(aiuCodes, 'all'),
      fetchByScope(nonAiuCodes, 'storage_only'),
    ]).then(([aiuList, nonAiuList]) => {
      const merged = [...(aiuList || []), ...(nonAiuList || [])];
      const uniq = new Map<string, any>();
      merged.forEach((it: any) => {
        const containerCode = String(it?.container_code ?? '').trim();
        const locationCode = String(it?.location_code ?? '').trim();
        const key = `${containerCode}|${locationCode}`;
        if (!containerCode) return;
        if (!uniq.has(key)) uniq.set(key, it);
      });
      const mapped = Array.from(uniq.values()).map((it: any) => ({
        container_code: it?.container_code,
        location_code: it?.location_code,
        container_qty: undefined,
        is_single_pack: undefined,
        picked_out_sum: 0,
        theoretical_remain: undefined,
      }));
      setBackendRightRows(mapped);
    }).catch(() => {
      setBackendRightRows([]);
    }).finally(() => { setBackendLoading(false); setBackendLoaded(true); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLeftRows, rows, isReadonly]);

  // 加载容器内容汇总（不限定目标物，用于显示总数量与明细）
  useEffect(() => {
    if (isReadonly) {
      // 只读模式不依赖仓位表与容器内容的实时汇总
      setContentsSummaryMap({});
      return;
    }
    const arr = (backendRightRows.length > 0 ? backendRightRows : rightRows);
    const containerCodes = Array.from(new Set(arr.map((r: any) => String(r?.container_code || '').trim()).filter(Boolean)));
    if (containerCodes.length === 0) {
      setContentsSummaryMap({});
      return;
    }
    setContentsLoading(true);
    request.post('/api/v1/storage/containers/contents/summary/', {
      container_codes: containerCodes
    }).then((res: any) => {
      const list = Array.isArray(res?.data?.results) ? res.data.results : (Array.isArray(res?.results) ? res.results : []);
      const map: Record<string, any[]> = {};
      list.forEach((row: any) => {
        const code = String(row?.container_code || '').trim();
        const items = Array.isArray(row?.contents_summary) ? row.contents_summary : [];
        map[code] = items;
      });
      setContentsSummaryMap(map);
    }).catch(() => {
      setContentsSummaryMap({});
    }).finally(() => setContentsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendRightRows, rightRows, isReadonly]);

  // 最终右侧数据：优先使用后端结果；若后端为空则使用前端基于 pick_rows 的推导
  const finalRightRows = backendLoaded ? backendRightRows : rightRows;

  // 右侧分页切片（依赖 rightRows）
  const rightPagedRows = useMemo(() => {
    const start = (rightPage - 1) * rightPageSize;
    return (backendLoaded ? backendRightRows : rightRows).slice(start, start + rightPageSize);
  }, [backendRightRows, backendLoaded, rightRows, rightPage, rightPageSize]);

  // 本地拣货记录（定义于文件顶部，避免 TDZ）

  // 基于“拣货记录”实时汇总各容器已拣出数量
  const pickedByContainer = useMemo(() => {
    const map: Record<string, number> = {};
    (pickLogs || []).forEach((rec) => {
      const code = String(rec?.container_code || '').trim();
      const qty = Number(rec?.qty || 0);
      if (!code || !(qty > 0)) return;
      map[code] = (map[code] || 0) + qty;
    });
    return map;
  }, [pickLogs]);

  // 计算展示用行：覆盖 picked_out_sum 与理论余量
  const displayRows = useMemo(() => {
    const base = backendLoaded ? backendRightRows : rightRows;
    return base.map((r: any) => {
      const code = String(r?.container_code || '').trim();
      const items = contentsSummaryMap[code] || [];
      const totalIn = items.reduce((s: number, it: any) => s + Number(it?.qty ?? 0), 0);
      const picked = Number((pickedByContainer[code] ?? r?.picked_out_sum ?? 0) || 0);
      // 理论余量允许为负：= 容器内数量 - 已拣出
      const remain = totalIn - picked;
      return {
        ...r,
        picked_out_sum_eff: picked,
        theoretical_remain_eff: remain,
        container_qty_eff: totalIn,
      };
    });
  }, [backendLoaded, backendRightRows, rightRows, contentsSummaryMap, pickedByContainer]);

  const displayRowsPaged = useMemo(() => {
    const start = (rightPage - 1) * rightPageSize;
    return displayRows.slice(start, start + rightPageSize);
  }, [displayRows, rightPage, rightPageSize]);

  // 手动输入模式：开关打开时，关闭“扫描即提交”，改为点击“提交”才判定
  const [manualMode, setManualMode] = useState<boolean>(false);
  const containerInputRef = useRef<any>(null);

  // 拣货记录统计（数量/种类/容器数）
  const pickStats = useMemo(() => {
    const totalQty = (pickLogs || []).reduce((s, r) => s + Number(r?.qty || 0), 0);
    const kindSet = new Set<string>();
    const contSet = new Set<string>();
    (pickLogs || []).forEach((r) => {
      const unit = String(r?.target_code || '').trim();
      const cont = String(r?.container_code || '').trim();
      if (unit) kindSet.add(unit);
      if (cont) contSet.add(cont);
    });
    return { totalQty, kinds: kindSet.size, containers: contSet.size };
  }, [pickLogs]);

  // 编辑/删除 拣货记录
  const [editVisible, setEditVisible] = useState<boolean>(false);
  const [editId, setEditId] = useState<string>('');
  const [editForm, setEditForm] = useState<{ container_code?: string; target_code?: string; qty?: number; operated_at?: string }>({});
  const openEdit = (rec: any) => {
    setEditId(String(rec?.id || ''));
    setEditForm({
      container_code: String(rec?.container_code || ''),
      target_code: String(rec?.target_code || ''),
      qty: Number(rec?.qty || 0),
      operated_at: String(rec?.operated_at || ''),
    });
    setEditVisible(true);
  };
  const submitEdit = () => {
    const qtyNum = Number(editForm.qty || 0);
    if (!(qtyNum > 0)) { message.warning('数量必须大于0'); return; }
    setPickLogs(prev => prev.map(r => (String(r.id) === editId ? { ...r, qty: qtyNum } : r)));
    setEditVisible(false);
  };
  const removeRecord = (rec: any) => {
    Modal.confirm({
      title: '确认删除该拣货记录？',
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => {
        setPickLogs(prev => prev.filter(r => String(r.id) !== String(rec?.id || '')));
      }
    });
  };

  // 右侧标题右侧统计（依赖 rightRows）
  const rightHeaderRight = useMemo(() => {
    const arr = displayRows;
    const locSet = new Set<string>();
    const contSet = new Set<string>();
    let totalInAll = 0;
    arr.forEach((r: any) => {
      const loc = String(r?.location_code ?? '').trim();
      const cont = String(r?.container_code ?? '').trim();
      if (loc) locSet.add(loc);
      if (cont) contSet.add(cont);
      totalInAll += Number(r?.container_qty_eff ?? 0);
    });
    const totalPickedAll = (pickLogs || []).reduce((s: number, rec) => s + Number((rec as any)?.qty || 0), 0);
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        <span>涉及仓位：{locSet.size}</span>
        <span style={vSepStyle} />
        <span>涉及容器：{contSet.size}</span>
        <span style={vSepStyle} />
        <span>容器内数量：{totalInAll}</span>
        <span style={vSepStyle} />
        <span>已拣货数量：{totalPickedAll}</span>
      </span>
    );
  }, [displayRows, vSepStyle, pickLogs]);

  // 右侧是否为空（用于禁用试拣）
  const isRightEmpty = useMemo(() => {
    return displayRows.length === 0;
  }, [displayRows.length]);

  // 试拣结果：是否可全部拣出（无非目标物、无超量）→ 控制“全部拣出”按钮
  const [canPickAllContainers, setCanPickAllContainers] = useState<boolean>(false);

  // 试拣：判断右侧所有容器是否可整箱（整容器）拣出
  const [trialLoading, setTrialLoading] = useState<boolean>(false);
  // 本地拣货记录（UI用）

  // 工具：确保某容器的内容汇总已就绪（必要时单独加载）
  const ensureContainerSummary = async (code: string) => {
    const c = String(code || '').trim();
    if (!c) return [];
    if (contentsSummaryMap[c]) return contentsSummaryMap[c] || [];
    try {
      const resp = await request.post('/api/v1/storage/containers/contents/summary/', { container_codes: [c] });
      const list = Array.isArray(resp?.data?.results) ? resp.data.results : [];
      const map: Record<string, any[]> = { ...contentsSummaryMap };
      list.forEach((row: any) => {
        const k = String(row?.container_code || '').trim();
        const items = Array.isArray(row?.contents_summary) ? row.contents_summary : [];
        map[k] = items;
      });
      setContentsSummaryMap(map);
      return map[c] || [];
    } catch {
      return [];
    }
  };

  // 自动判断：扫描容器后判定是否可全部拣出
  const [lastAutoJudgedContainer, setLastAutoJudgedContainer] = useState<string>('');
  // 超量判定弹窗（支持扫描记录“已拣出”）
  const [overPickVisible, setOverPickVisible] = useState<boolean>(false);
  const [overPickOffShelf, setOverPickOffShelf] = useState<boolean>(false);
  const [overPickContainer, setOverPickContainer] = useState<string>('');
  const [overPickRows, setOverPickRows] = useState<Array<{ unit: string; mark: string; have: number; need: number; picked: number }>>([]);
  const [overPickScan, setOverPickScan] = useState<string>('');
  const overScanRef = useRef<any>(null);
  // 含非目标物场景下的可编辑弹窗（与“超量”风格一致）
  const [ntPickVisible, setNtPickVisible] = useState<boolean>(false);
  const [ntPickOffShelf, setNtPickOffShelf] = useState<boolean>(false);
  const [ntPickContainer, setNtPickContainer] = useState<string>('');
  const [ntPickRows, setNtPickRows] = useState<Array<{ unit: string; mark: string; have: number; need: number; picked: number; isNonTarget?: boolean }>>([]);
  const [ntPickScan, setNtPickScan] = useState<string>('');
  const ntScanRef = useRef<any>(null);
  const submitOverScan = () => {
    const raw = String(overPickScan || '');
    const code = raw.trim();
    if (!code) {
      setOverPickScan('');
      setTimeout(() => overScanRef.current?.focus?.(), 0);
      return;
    }
    setOverPickRows(prev => {
      const next = [...prev];
      // 统一大小写与空白
      const scanned = code.toUpperCase();
      // 匹配规则：
      // 1) 直接等于库存单元编码
      // 2) 命中分拣标识（分隔符 " / " 拆分后任一等于）
      let idx = next.findIndex(r => String(r.unit || '').trim().toUpperCase() === scanned);
      if (idx < 0) {
        idx = next.findIndex(r => {
          const mark = String(r.mark || '').trim();
          if (!mark) return false;
          // 允许完整包含或“ / ”拆分后等值匹配
          const tokens = mark.split('/').map(s => s.trim().toUpperCase()).filter(Boolean);
          if (tokens.includes(scanned)) return true;
          // 兼容扫描码为标识的一部分（避免误伤，限定长度大于等于3时再模糊）
          if (scanned.length >= 3 && mark.toUpperCase().includes(scanned)) return true;
          return false;
        });
      }
      if (idx >= 0) next[idx] = { ...next[idx], picked: Number(next[idx].picked || 0) + 1 };
      else message.warning('未匹配到对应目标物');
      return next;
    });
    setOverPickScan('');
    setTimeout(() => overScanRef.current?.focus?.(), 0);
  };
  const submitNtScan = () => {
    const raw = String(ntPickScan || '');
    const code = raw.trim();
    if (!code) {
      setNtPickScan('');
      setTimeout(() => ntScanRef.current?.focus?.(), 0);
      return;
    }
    setNtPickRows(prev => {
      const next = [...prev];
      const scanned = code.toUpperCase();
      let idx = next.findIndex(r => String(r.unit || '').trim().toUpperCase() === scanned);
      if (idx < 0) {
        idx = next.findIndex(r => {
          const mark = String(r.mark || '').trim();
          if (!mark) return false;
          const tokens = mark.split('/').map(s => s.trim().toUpperCase()).filter(Boolean);
          if (tokens.includes(scanned)) return true;
          if (scanned.length >= 3 && mark.toUpperCase().includes(scanned)) return true;
          return false;
        });
      }
      if (idx >= 0) {
        if (next[idx]?.isNonTarget) {
          message.error('扫描到非目标物，禁止拣出该行');
        } else {
          next[idx] = { ...next[idx], picked: Number(next[idx].picked || 0) + 1 };
        }
      } else {
        message.warning('未匹配到对应目标物');
      }
      return next;
    });
    setNtPickScan('');
    setTimeout(() => ntScanRef.current?.focus?.(), 0);
  };
  const judgeContainerByCode = async (code: string) => {
    const cRaw = String(code || '').trim();
    if (!cRaw) return;
    // 统一大小写：优先解析为“仓位表中的规范容器编码”（忽略大小写与空格）
    const upper = cRaw.toUpperCase();
    const codeMap = new Map<string, string>((displayRows || []).map((r: any) => {
      const orig = String(r?.container_code || '').trim();
      return [orig.toUpperCase(), orig];
    }));
    const c = codeMap.get(upper) || cRaw;
    // 先校验：扫描容器是否在“仓位表”的容器集合（跨分页、全量）
    try {
      const allowedCodes = new Set<string>(
        (displayRows || [])
          .map((r: any) => String(r?.container_code || '').trim().toUpperCase())
          .filter(Boolean)
      );
      if (!allowedCodes.has(String(c).toUpperCase())) {
        message.warning('该容器不在仓位表记录中，已禁止后续操作');
        setLastAutoJudgedContainer(c);
        try { onChange('container_code', ''); } catch {}
        return;
      }
    } catch {}
    // 与上次扫描相同？询问是否继续
    if (lastAutoJudgedContainer === c) {
      const proceed = await new Promise<boolean>((resolve) => {
        Modal.confirm({
          title: `与上次扫描的容器相同（容器：${c}），是否继续提交？`,
          okText: '继续提交',
          cancelText: '取消',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!proceed) {
        try { onChange('container_code', ''); } catch {}
        try { containerInputRef.current?.focus?.(); } catch {}
        return;
      }
    }
    // 需要有左侧选择集（用于目标物差额）
    if (!selectedLeftRows || selectedLeftRows.length === 0) return;

    // 准备 needByTarget（拣货差额）
    const needByTarget: Record<string, number> = { ...adjustedNeedByTarget };
    const targetKeys = Object.keys(needByTarget).filter(Boolean);
    if (targetKeys.length === 0) return;

    // 确保容器内容已加载
    const items = (contentsSummaryMap[c] && Array.isArray(contentsSummaryMap[c]))
      ? contentsSummaryMap[c]
      : await ensureContainerSummary(c);
    if (!items || items.length === 0) return;

    // 判断是否“已全部拣出”：逐条记录 remain<=0 才算全部拣出
    try {
      const unitPickedMap: Record<string, number> = {};
      (pickLogs || [])
        .filter(r => String(r?.container_code || '').trim() === c)
        .forEach(r => {
          const u = String(r?.target_code || '').trim();
          const q = Number(r?.qty || 0);
          if (!u || !(q > 0)) return;
          unitPickedMap[u] = (unitPickedMap[u] || 0) + q;
        });
      const allRemainNonPositive = (items || []).every((it: any) => {
        const u = String(it?.unit_code || '').trim();
        const qty = Number(it?.qty || 0);
        const pickedForThis = Number(unitPickedMap[u] || 0);
        const remain = qty - pickedForThis;
        return remain <= 0;
      });
      if (allRemainNonPositive) {
        const proceed = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: `此容器已全部拣出（容器：${c}），是否继续提交？`,
            okText: '继续提交',
            cancelText: '取消',
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (!proceed) {
          try { onChange('container_code', ''); } catch {}
          try { containerInputRef.current?.focus?.(); } catch {}
          return;
        }
      }
    } catch {}

    // 汇总容器中每个 unit 的数量
    const qtyByUnit: Record<string, number> = {};
    items.forEach((it: any) => {
      const u = String(it?.unit_code || '').trim();
      const q = Number(it?.qty || 0);
      if (!u) return;
      qtyByUnit[u] = (qtyByUnit[u] || 0) + q;
    });

    // 规则判定
    // 1) 是否包含非目标物
    const nonTargets = Object.keys(qtyByUnit).filter(u => !targetKeys.includes(u));
    if (nonTargets.length > 0) {
      setLastAutoJudgedContainer(c);
      try { onChange('container_code', ''); } catch {}
      // 使用与“超量”一致的可编辑弹窗：展示容器全部内容
      // 构建分拣标识
      const markMap: Record<string, string> = {};
      try {
        (items || []).forEach((it: any) => {
          const u = String(it?.unit_code || '').trim();
          if (!u) return;
          if (Array.isArray(it?.sorting_codes) && it.sorting_codes.length > 0) {
            markMap[u] = it.sorting_codes.filter(Boolean).join(' / ');
          } else if (it?.sorting_code) {
            markMap[u] = String(it.sorting_code);
          }
        });
      } catch {}
      const rowsForNtModal = (items || []).map((it: any) => {
        const u = String(it?.unit_code || '').trim();
        const have = Number(it?.qty || 0);
        const need = Number(needByTarget[u] || 0); // 非目标物的 need 默认为 0
        const mark = markMap[u] || '-';
        const isNonTarget = !targetKeys.includes(u);
        return { unit: u, mark, have, need, picked: 0, isNonTarget };
      });
      setNtPickContainer(c);
      setNtPickRows(rowsForNtModal);
      setNtPickScan('');
      setNtPickVisible(true);
      setNtPickOffShelf(false);
      setTimeout(() => ntScanRef.current?.focus?.(), 0);
      return;
    }

    // 2) 目标物数量超过拣货差额？
    const overList: Array<{ unit: string; have: number; need: number }> = [];
    targetKeys.forEach((u) => {
      const have = Number(qtyByUnit[u] || 0);
      const need = Number(needByTarget[u] || 0);
      if (have > need) {
        overList.push({ unit: u, have, need });
      }
    });
    if (overList.length > 0) {
      setLastAutoJudgedContainer(c);
      try { onChange('container_code', ''); } catch {}
      // 为该容器内的目标物构建分拣标识映射
      const markMap: Record<string, string> = {};
      try {
        (items || []).forEach((it: any) => {
          const u = String(it?.unit_code || '').trim();
          if (!u) return;
          if (Array.isArray(it?.sorting_codes) && it.sorting_codes.length > 0) {
            markMap[u] = it.sorting_codes.filter(Boolean).join(' / ');
          } else if (it?.sorting_code) {
            markMap[u] = String(it.sorting_code);
          }
        });
      } catch {}
      // 打开可扫描编辑的“超量”弹窗（已拣出默认为0），显示容器中的所有内容
      const rowsForModal = (items || []).map((it: any) => {
        const u = String(it?.unit_code || '').trim();
        const have = Number(it?.qty || 0);
        const need = Number(needByTarget[u] || 0);
        const mark = markMap[u] || '-';
        return { unit: u, mark, have, need, picked: 0 };
      });
      setOverPickContainer(c);
      setOverPickRows(rowsForModal);
      setOverPickScan('');
      setOverPickVisible(true);
      setOverPickOffShelf(false);
      setTimeout(() => overScanRef.current?.focus?.(), 0);
      return;
    }

    // 3) 否则可全部拣出：将容器中的所有内容物加入拣货记录
    const allRows = items.map((it: any) => {
      const u = String(it?.unit_code || '').trim();
      const q = Number(it?.qty || 0);
      let mark = '-';
      try {
        if (Array.isArray(it?.sorting_codes) && it.sorting_codes.length > 0) {
          mark = it.sorting_codes.filter(Boolean).join(' / ');
        } else if (it?.sorting_code) {
          mark = String(it.sorting_code);
        }
      } catch {}
      return { unit: u, qty: q, mark };
    });
    if (allRows.length > 0) {
      const now = new Date();
      const newLogs = allRows.map((r) => ({
        id: `${now.getTime()}_${r.unit}_${Math.floor(Math.random()*100000)}`,
        container_code: c,
        target_code: r.unit,
        sorting_mark: r.mark,
        qty: r.qty,
        operated_at: now.toLocaleString(),
        off_shelf: true,
      }));
      setPickLogs(prev => [...newLogs, ...prev]);
      message.success('容器可全部拣出，已将内容物全部加入拣货记录');
    }
    setLastAutoJudgedContainer(c);
    try { onChange('container_code', ''); } catch {}
    try { containerInputRef.current?.focus?.(); } catch {}
  };

  useEffect(() => {
    (async () => {
      const code = String(metrics?.container_code || '').trim();
      if (!code) return;
      if (manualMode) return; // 手动模式下不自动提交
      await judgeContainerByCode(code);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metrics?.container_code, contentsSummaryMap, selectedLeftRows, displayRows, adjustedNeedByTarget, manualMode]);
  const handleAddRecord = () => {
    const container = String(metrics?.container_code || '').trim();
    const target = String(metrics?.target_code || '').trim();
    const qtyNum = Number(metrics?.pick_qty || 0);
    if (!container || !target || !(qtyNum > 0)) {
      message.warning('请填写完整：扫描容器、目标物、数量>0');
      return;
    }
    const now = new Date();
    const rec = {
      id: `${now.getTime()}_${Math.floor(Math.random() * 100000)}`,
      container_code: container,
      target_code: target,
      sorting_mark: '', // 占位，可后续联动补充
      qty: qtyNum,
      operated_at: now.toLocaleString(),
    };
    setPickLogs(prev => [rec, ...prev]);
    // 入表后清空目标与数量，保留容器
    try {
      onChange('target_code', '');
      onChange('pick_qty', undefined);
    } catch {}
  };
  const handleClearAllRecords = () => {
    Modal.confirm({
      title: '确认删除全部拣货记录？',
      content: '该操作不可撤销。',
      okText: '删除全部',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => setPickLogs([]),
    });
  };
  const onTryPickAllContainers = async () => {
    try {
      // 1) 选中目标的“还需拣数量” needByTarget（实时，基于 leftAdjustedRows 与 selection）
      const needByTarget: Record<string, number> = { ...adjustedNeedByTarget };
      const targetCodes = Object.keys(needByTarget).filter(Boolean);
      if (targetCodes.length === 0) {
        return;
      }
      // 2) 右侧容器集合
      const containerCodes = Array.from(new Set((backendRightRows.length > 0 ? backendRightRows : rightRows).map((r: any) => String(r?.container_code || '').trim()).filter(Boolean)));
      if (containerCodes.length === 0) {
        return;
      }
      setTrialLoading(true);
      // 3) 调用后端批量汇总接口（不限定目标物，拿到容器内所有库存单元以便统计“非目标物”）
      const resp = await request.post('/api/v1/storage/containers/contents/summary/', {
        container_codes: containerCodes
      });
      const list = Array.isArray(resp?.data?.results) ? resp.data.results : [];
      // 4) 聚合所有容器中“目标物”的可取量 haveByTarget 与 “非目标物”汇总 nonTargetMap
      const haveByTarget: Record<string, number> = {};
      const targetSortingMark: Record<string, string> = {};
      const nonTargetMap: Record<string, number> = {};
      list.forEach((row: any) => {
        const arr = Array.isArray(row?.contents_summary) ? row.contents_summary : [];
        arr.forEach((it: any) => {
          const u = String(it?.unit_code || '').trim();
          const q = Number(it?.qty || 0);
          if (!u) return;
          if (targetCodes.includes(u)) {
            haveByTarget[u] = (haveByTarget[u] || 0) + q;
            // 记录目标物的分拣标识（优先 sorting_codes，其次 sorting_code）
            try {
              let mark = targetSortingMark[u];
              if (!mark) {
                if (Array.isArray(it?.sorting_codes) && it.sorting_codes.length > 0) {
                  mark = it.sorting_codes.filter(Boolean).join(' / ');
                } else if (it?.sorting_code) {
                  mark = String(it.sorting_code);
                }
                if (mark) targetSortingMark[u] = mark;
              }
            } catch {}
          } else {
            nonTargetMap[u] = (nonTargetMap[u] || 0) + q;
          }
        });
      });
      // 5) 判定：根据需求定义
      // A) 若所有记录 have <= need：容器可全部拣出；
      //    A1) 若全部相等：提示“容器可全部拣出，全部记录可拣数量等于应拣数量”
      //    A2) 若存在 have < need：提示“容器可全部拣出，部分记录可拣数量不足...（建议项）”
      // B) 若存在 have > need：提示“容器不可全部拣出，部分记录可拣数量超过应拣数量”
      const overKeys: string[] = [];
      const underKeys: string[] = [];
      const detailRows: Array<{ key: string; need: number; have: number; shortage: number; excess: number; status: string }> = [];
      targetCodes.forEach((t) => {
        const have = Number(haveByTarget[t] || 0);
        const need = Number(needByTarget[t] || 0);
        if (have > need) overKeys.push(t);
        if (have < need) underKeys.push(t);
        const shortage = Math.max(need - have, 0);
        const excess = Math.max(have - need, 0);
        detailRows.push({ key: t, need, have, shortage, excess, status: have === need ? '= 刚好' : (have > need ? '> 超量' : '< 不足') });
      });
      const shortageTotal = targetCodes.reduce((s, t) => s + Math.max((needByTarget[t] || 0) - (haveByTarget[t] || 0), 0), 0);
      const excessTotal = targetCodes.reduce((s, t) => s + Math.max((haveByTarget[t] || 0) - (needByTarget[t] || 0), 0), 0);
      // 按要求排序：不足(<) → 刚好(=) → 超量(>)
      const statusRank = (s: string) => (s.startsWith('<') ? 0 : (s.startsWith('=') ? 1 : 2));
      const sortedRows = [...detailRows].sort((a, b) => statusRank(a.status) - statusRank(b.status));

      // 更新“全部拣出”可用状态：无非目标物、无超量
      try {
        const hasNonTargets = Object.keys(nonTargetMap).length > 0;
        setCanPickAllContainers(!hasNonTargets && overKeys.length === 0);
      } catch {}

      // 组装统一的上下版式内容
      const renderTitle = (tone: 'success'|'info'|'warn') => {
        // 芯片显示逻辑：
        // 1) 只要包含非目标物 → “不可全部拣出”（红）
        // 2) 不包含非目标物，存在任意“超量” → “不可全部拣出”（红）
        // 3) 不包含非目标物，且无超量，且全部“正好” → “可全部拣出-正好”（绿）
        // 4) 不包含非目标物，且无超量，且存在“不足” → “可全部拣出-有不足”（浅黄）
        const hasNonTargets = Object.keys(nonTargetMap).length > 0;
        const hasOver = overKeys.length > 0;
        const hasUnder = underKeys.length > 0;

        let chipLabel = '';
        let chipBg = '';
        let chipBorder = '';
        let chipText = '';
        if (hasNonTargets || hasOver) {
          const reasons: string[] = [];
          if (hasOver) reasons.push('存在超量');
          if (hasNonTargets) reasons.push('存在非目标物');
          chipLabel = reasons.length > 0 ? `不可全部拣出（${reasons.join('、')}）` : '不可全部拣出';
          chipBg = '#fff1f0'; chipBorder = '#ffa39e'; chipText = '#a8071a';
        } else if (!hasUnder) {
          chipLabel = '可全部拣出-正好';
          chipBg = '#f6ffed'; chipBorder = '#b7eb8f'; chipText = '#135200';
        } else {
          chipLabel = '可全部拣出-有不足';
          // 浅黄色方案
          chipBg = '#fffbe6'; chipBorder = '#ffe58f'; chipText = '#ad6800';
        }
        return (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%' }}>
            <span>试拣结果</span>
            <span style={{ color: '#595959', fontWeight: 600 }}>不足合计：{shortageTotal} ｜ 超额合计：{excessTotal}</span>
            <span
              style={{
                display: 'inline-block',
                padding: '2px 8px',
                borderRadius: 999,
                background: chipBg,
                border: `1px solid ${chipBorder}`,
                color: chipText,
                fontWeight: 600
              }}
            >
              {chipLabel}
            </span>
          </div>
        );
      };
      const renderContent = (tone: 'success'|'info'|'warn') => {
        const tones: Record<string, { bg: string; border: string; text: string; chipBg: string; chipBorder: string; chipText: string }> = {
          // success：绿色
          success: { bg: '#ffffff', border: '#b7eb8f', text: '#135200', chipBg: '#f6ffed', chipBorder: '#b7eb8f', chipText: '#135200' },
          // info 改为“有不足”浅黄色主题
          info:    { bg: '#ffffff', border: '#ffe58f', text: '#ad6800', chipBg: '#fffbe6', chipBorder: '#ffe58f', chipText: '#ad6800' },
          // warn：红色
          warn:    { bg: '#ffffff', border: '#ffa39e', text: '#a8071a', chipBg: '#fff1f0', chipBorder: '#ffa39e', chipText: '#a8071a' },
        };
        const t = tones[tone];
        return (
          <div style={{ background: t.bg, border: `1px solid ${t.border}`, borderRadius: 6, padding: 8 }}>
            {/* 下部：对比明细 */}
            <div style={{ marginTop: 6 }}>
              <div style={{ fontWeight: 600, marginBottom: 4, textAlign: 'center' }}>对比明细</div>
              {/* 不足提示 */}
              {(underKeys.length > 0) && (
                <div style={{ margin: '4px 0 8px', background: '#e6f4ff', border: '1px solid #91caff', borderRadius: 6, padding: 8, color: '#0958d9' }}>
                  若有数量不足，请查看是否有已入库未创建库存容器的产品，或指令数量是否超过了可用库存数量。
                </div>
              )}
              {/* 目标物列表 */}
              <div style={{ marginBottom: 10 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ border: '1px solid #d9d9d9', padding: '4px 8px', textAlign: 'left' }}>目标物</th>
                      <th style={{ border: '1px solid #d9d9d9', padding: '4px 8px', textAlign: 'left' }}>分拣标识</th>
                      <th style={{ border: '1px solid #d9d9d9', padding: '4px 8px', textAlign: 'right' }}>可拣</th>
                      <th style={{ border: '1px solid #d9d9d9', padding: '4px 8px', textAlign: 'right' }}>应拣</th>
                      <th style={{ border: '1px solid #d9d9d9', padding: '4px 8px', textAlign: 'left' }}>对比结果</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((r) => {
                      // 行背景色：不足=浅黄、正好=浅绿、超量=浅红
                      const isUnder = r.status.startsWith('<');
                      const isEqual = r.status.startsWith('=');
                      const isOver  = r.status.startsWith('>');
                      const rowBg   = isUnder ? '#fffbe6' : (isEqual ? '#f6ffed' : (isOver ? '#fff1f0' : 'transparent'));
                      const resultText = isEqual
                        ? '= 刚好'
                        : (isOver ? `> 超量  Qty:${r.excess}` : `< 不足  Qty:${r.shortage}`);
                      return (
                        <tr key={r.key} style={{ background: rowBg }}>
                          <td style={{ border: '1px solid #d9d9d9', padding: '4px 8px', fontFamily: 'monospace', color: '#595959' }}>{r.key}</td>
                          <td style={{ border: '1px solid #d9d9d9', padding: '4px 8px' }}>{targetSortingMark[r.key] || '-'}</td>
                          <td style={{ border: '1px solid #d9d9d9', padding: '4px 8px', textAlign: 'right' }}>{r.have}</td>
                          <td style={{ border: '1px solid #d9d9d9', padding: '4px 8px', textAlign: 'right' }}>{r.need}</td>
                          <td style={{ border: '1px solid #d9d9d9', padding: '4px 8px' }}>{resultText}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* 非目标物列表 */}
              {list.length > 0 && (
                <div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ border: '1px solid #d9d9d9', padding: '4px 8px', textAlign: 'left' }}>非目标物</th>
                        <th style={{ border: '1px solid #d9d9d9', padding: '4px 8px', textAlign: 'left' }}>分拣标识</th>
                        <th style={{ border: '1px solid #d9d9d9', padding: '4px 8px', textAlign: 'left' }}>所在容器</th>
                        <th style={{ border: '1px solid #d9d9d9', padding: '4px 8px', textAlign: 'right' }}>数量</th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.flatMap((row: any) => {
                        const cont = String(row?.container_code || '');
                        const arr = Array.isArray(row?.contents_summary) ? row.contents_summary : [];
                        return arr
                          .filter((it: any) => !targetCodes.includes(String(it?.unit_code || '').trim()))
                          .map((it: any, idx: number) => {
                            const u = String(it?.unit_code || '').trim();
                            const q = Number(it?.qty || 0);
                            // 分拣标识：尝试使用 it.sorting_codes / it.sorting_code
                            let sortingMark = '-';
                            try {
                              if (Array.isArray(it?.sorting_codes) && it.sorting_codes.length > 0) {
                                sortingMark = it.sorting_codes.filter(Boolean).join(' / ');
                              } else if (it?.sorting_code) {
                                sortingMark = String(it.sorting_code);
                              }
                            } catch {}
                            return (
                              <tr key={`${cont}|${u}|${idx}`}>
                                <td style={{ border: '1px solid #d9d9d9', padding: '4px 8px', fontFamily: 'monospace', color: '#595959' }}>{u}</td>
                                <td style={{ border: '1px solid #d9d9d9', padding: '4px 8px' }}>{sortingMark}</td>
                                <td style={{ border: '1px solid #d9d9d9', padding: '4px 8px' }}>{cont || '-'}</td>
                                <td style={{ border: '1px solid #d9d9d9', padding: '4px 8px', textAlign: 'right' }}>{q}</td>
                              </tr>
                            );
                          });
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        );
      };

      const hasNonTargets = Object.keys(nonTargetMap || {}).length > 0;
      if (hasNonTargets || overKeys.length > 0) {
        Modal.warning({ title: renderTitle('warn'), content: renderContent('warn'), okText: '知道了', width: 980 });
      } else if (underKeys.length > 0) {
        Modal.info({ title: renderTitle('info'), content: renderContent('info'), okText: '知道了', width: 980 });
      } else {
        Modal.success({ title: renderTitle('success'), content: renderContent('success'), okText: '知道了', width: 980 });
      }
    } catch (e) {
      Modal.error({
        title: '试拣失败',
        content: '试拣校验失败，请稍后重试',
        okText: '关闭'
      });
    } finally {
      setTrialLoading(false);
    }
  };

  // 打印当前仓位表记录（按当前分页）
  const onPrintRightTable = () => {
    try {
      const rows = displayRowsPaged || [];
      const locSet = new Set<string>();
      const contSet = new Set<string>();
      rows.forEach((r:any) => {
        const loc = String(r?.location_code || '').trim();
        const cont = String(r?.container_code || '').trim();
        if (loc) locSet.add(loc);
        if (cont) contSet.add(cont);
      });
      // 使用已获取的指令系统编码（若不可得则回退为“指令#-”）
      const instrCode = String(instructionCode || '指令#-');
      const printTime = (() => {
        try { return new Date().toLocaleString(); } catch { return ''; }
      })();
      const htmlHead = `
        <head>
          <meta charset="utf-8" />
          <title>仓位表打印</title>
          <style>
            @page { size: 4in 6in; margin: 0.15in; }
            body {
              margin: 0;
              padding: 0;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", "PingFang SC", "Microsoft YaHei", sans-serif;
              font-weight: 700;
            }
            .page { padding: 0; }
            table { width: 100%; border-collapse: collapse; }
            /* 边框加深，便于 4x6 打印识别 */
            th, td { border: 1px solid #595959; padding: 0; font-size: 10px; font-weight: 700; }
            th { background: #fafafa; text-align: left; font-weight: 800; }
            .title-bar { display:flex; flex-direction:column; gap: 6px; margin-bottom: 8px; }
            .row1 { display:flex; align-items:center; justify-content:flex-start; gap: 10px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
            .row2 { display:flex; align-items:center; justify-content:space-between; gap: 10px; white-space: nowrap; }
            .title { font-size: 11px; font-weight: 700; }
            .meta { color: #595959; font-size: 10px; }
            .vsep { display: inline-block; vertical-align: middle; margin: 0 8px; height: 12px; border-left: 1px solid #8c8c8c; }
            .checkbox-cell { text-align:center; }
            .checkbox-box { display:inline-block; width: 12px; height: 12px; border: 1px solid #000; }
            .hand-cell { height: 24px; min-height: 24px; }
            .mark-label { font-size: 10px; color: #595959; margin-bottom: 4px; }
            /* 列宽：压缩“容器/理论余量”，尽量给“拣出数量”更多空间（适配 4in 宽度） */
            .col-loc { width: 40px; }
            .col-cont { width: 92px; word-break: break-all; }
            .col-remain { width: 34px; text-align: right; }
            .col-check { width: 38px; text-align: center; }
            .col-qty { width: 120px; }
            .remain-head { display:inline-block; line-height: 1.05; white-space: normal; }
            /* 方案A：不改列宽，单独放大三列“数据文本” */
            td.col-loc { font-size: 14px; line-height: 1.1; }
            td.col-cont { font-size: 12px; line-height: 1.1; }
            td.col-remain { font-size: 14px; line-height: 1.1; }
          </style>
        </head>
      `;
      const htmlBody = `
        <div class="page">
          <div class="title-bar">
            <div class="row1">
              <span class="title">仓位表打印</span>
              <span class="meta">涉及仓位：<b>${locSet.size}</b></span>
              <span class="vsep"></span>
              <span class="meta">涉及容器：<b>${contSet.size}</b></span>
            </div>
            <div class="row2">
              <span class="meta">指令：<b style="color:#000;">${instrCode}</b></span>
              <span class="meta">打印时间：${printTime}</span>
            </div>
          </div>
        <table>
          <thead>
            <tr>
              <th class="col-loc">仓位</th>
              <th class="col-cont">容器</th>
              <th class="col-remain"><span class="remain-head">理论<br/>余量</span></th>
              <th class="col-check">全拣</th>
              <th class="col-qty">拣出数量</th>
            </tr>
          </thead>
          <tbody>
            ${rows.map((r:any) => {
              const cont = String(r?.container_code || '');
              const loc = String(r?.location_code || '');
              const inQty = Number(r?.container_qty_eff ?? 0);
              const picked = Number(r?.picked_out_sum_eff ?? r?.picked_out_sum ?? 0);
              const remain = Number(r?.theoretical_remain_eff ?? r?.theoretical_remain ?? (inQty - picked));
              return `<tr>
                        <td class="col-loc">${loc}</td>
                        <td class="col-cont">${cont}</td>
                        <td class="col-remain">${remain}</td>
                        <td class="col-check"><span class="checkbox-box"></span></td>
                        <td class="col-qty">
                          <div class="mark-label">标识：</div>
                          <div class="hand-cell"></div>
                        </td>
                      </tr>`;
            }).join('')}
          </tbody>
        </table>
        </div>
      `;
      const w = window.open('', 'print');
      if (!w) return;
      w.document.open();
      w.document.write(`<!DOCTYPE html><html>${htmlHead}<body>${htmlBody}</body></html>`);
      w.document.close();
      w.focus();
      try { (w as any).onafterprint = () => { try { w.close(); } catch {} }; } catch {}
      w.print();
      // w.close(); // onafterprint 更稳妥；手动关闭窗口可能被浏览器拦截
    } catch {
      message.error('打印失败，请重试');
    }
  };

  // 一键“全部拣出”：将当前仓位表所有容器的所有内容加入拣货记录（每个容器-目标物一行）
  const onPickAllContainers = async () => {
    const proceed = async () => {
      try {
        const arr = displayRows; // 使用当前仓位表（全部，不限制分页）
        const uniqueCodes = Array.from(new Set(arr.map((r:any) => String(r?.container_code || '').trim()).filter(Boolean)));
        // 确保每个容器内容已就绪
        for (const code of uniqueCodes) {
          if (!contentsSummaryMap[code]) {
            await ensureContainerSummary(code);
          }
        }
        const now = new Date();
        const newLogs: Array<any> = [];
        uniqueCodes.forEach((code) => {
          const items = contentsSummaryMap[code] || [];
          items.forEach((it: any) => {
            const unit = String(it?.unit_code || '').trim();
            const qty = Number(it?.qty || 0);
            if (!unit || !(qty > 0)) return;
            let mark = '-';
            try {
              if (Array.isArray(it?.sorting_codes) && it.sorting_codes.length > 0) {
                mark = it.sorting_codes.filter(Boolean).join(' / ');
              } else if (it?.sorting_code) {
                mark = String(it.sorting_code);
              }
            } catch {}
            newLogs.push({
              id: `${now.getTime()}_${unit}_${Math.floor(Math.random() * 100000)}`,
              container_code: code,
              target_code: unit,
              sorting_mark: mark,
              qty,
              operated_at: now.toLocaleString(),
              off_shelf: true,
            });
          });
        });
        if (newLogs.length > 0) {
          setPickLogs(prev => [...newLogs, ...prev]);
          message.success(`已加入拣货记录（全部拣出）：${newLogs.length} 条`);
        } else {
          message.info('没有可加入的容器内容');
        }
      } catch {
        message.error('全部拣出失败，请重试');
      }
    };

    if ((pickLogs || []).length > 0) {
      Modal.confirm({
        title: '确认覆盖现有拣货记录并执行全部拣出？',
        content: '该操作将清空当前的所有拣货记录，然后把仓位表中的容器内容全部加入。',
        okText: '执行',
        cancelText: '取消',
        onOk: async () => {
          setPickLogs([]);
          await proceed();
        }
      });
    } else {
      await proceed();
    }
  };

  return (
    <div className="pick-step-root" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <style>{`
        .pick-step-root .ant-table-tbody > tr.pick-row-done > td { background: #f6ffed !important; }
        .pick-step-root .ant-table-tbody > tr.pick-row-done.ant-table-row-selected > td { background: #d9f7be !important; }
        .pick-step-root .ant-table-tbody > tr.pick-row-over > td { color: #cf1322 !important; font-weight: 700 !important; }
        .pick-step-root .ant-table-tbody > tr.pick-row-over.ant-table-row-selected > td { color: #a8071a !important; font-weight: 700 !important; }
      `}</style>
      <div style={{ background:'#ffffff', border:'1px solid #f0f0f0', borderRadius: 6, padding: 8 }}>
        <div style={{ display:'flex', gap: 0 }}>
          {/* 左侧列表 */}
          <div
            style={{
              flex: 1,
              paddingRight: 12,
              background: '#f6fffb',
              borderRadius: 6,
              opacity: isReadonly ? 0.55 : 1,
              pointerEvents: isReadonly ? 'none' as any : 'auto',
              filter: isReadonly ? 'grayscale(0.15)' : undefined,
            }}
          >
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', margin: '0 0 8px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontWeight: 600 }}>拣货进度看板</span>
                <span style={{ color: '#d9d9d9', fontWeight: 400 }}>|</span>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '1px 6px',
                    border: (boardRate.rateValue != null && boardRate.rateValue >= 100) ? '1px solid #b7eb8f' : '1px solid #ffadd2',
                    background: (boardRate.rateValue != null && boardRate.rateValue >= 100) ? '#f6ffed' : '#fff0f6',
                    color: (boardRate.rateValue != null && boardRate.rateValue >= 100) ? '#389e0d' : '#c41d7f',
                    borderRadius: 4,
                    fontWeight: 700,
                    fontSize: 12,
                    lineHeight: '18px',
                  }}
                >
                  <span>完成率 {boardRate.rateText}</span>
                  {(boardRate.rateValue != null && boardRate.rateValue >= 100) ? (
                    <span style={{ color: '#389e0d', fontWeight: 900, fontSize: 18, lineHeight: '18px' }}>√</span>
                  ) : null}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 13, color: '#595959' }}>仅显示未完成记录</span>
                  <Switch checked={hideCompleted} onChange={setHideCompleted} size="small" />
                </span>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap: 12 }}>
                <div style={{ color: '#595959' }}>{leftHeaderRight}</div>
              </div>
            </div>
            <Table
              size="small"
              rowKey={(r:any) => {
                return getLeftRowKey(r);
              }}
              rowSelection={rowSelection}
              columns={leftColumns}
              dataSource={leftPagedAdjustedRows}
              rowClassName={(record:any) => {
                const diff = Number(record?.diff_qty ?? 0);
                if (diff < 0) return 'pick-row-done pick-row-over';
                if (diff <= 0) return 'pick-row-done';
                return '';
              }}
              pagination={false}
            />
            {debugVisible && (
              <div style={{ marginTop: 8, background: '#fff1f0', border: '1px dashed #ffa39e', borderRadius: 6, padding: 8, fontFamily: 'monospace', color: '#cf1322' }}>
                <div>调试信息：</div>
                <div>
                  rows={rows.length}；右rows={rightRows.length}；选中行={selectedLeftRows.length}；选中目标键数={selectedTargetKeySet.size}
                </div>
              </div>
            )}
            <div style={{ display:'flex', justifyContent:'flex-end', marginTop: 8 }}>
              <Pagination
                size="small"
                current={leftPage}
                pageSize={leftPageSize}
                total={leftFilteredRows.length}
                showSizeChanger
                showQuickJumper
                onChange={(p, ps) => {
                  setLeftPage(p);
                  setLeftPageSize(ps);
                }}
                showTotal={(total) => `共 ${total} 条`}
              />
            </div>
          </div>
          {/* 右侧列表 */}
          <div style={{ flex: 1, paddingLeft: 12, borderLeft: '2px solid #e8e8e8', background: '#fff7e6', borderRadius: 6 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', margin: '0 0 8px 0' }}>
              <div style={{ fontWeight: 600 }}>仓位表</div>
              {isReadonly ? (
                <Tag color="green" style={{ margin: 0 }}>已完成：仓位表已隐藏</Tag>
              ) : (
                <div style={{ display:'flex', alignItems:'center', margin: 0, padding: 0 }}>
                  <Button
                    size="small"
                    loading={trialLoading}
                    onClick={onTryPickAllContainers}
                    type="primary"
                    disabled={isRightEmpty}
                    style={{
                      background: isRightEmpty ? '#d9d9d9' : '#91caff',
                      borderColor: isRightEmpty ? '#d9d9d9' : '#91caff',
                      color: '#ffffff',
                      margin: 0,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 24,
                      lineHeight: '24px',
                      padding: '0 12px',
                      alignSelf: 'center'
                    }}
                  >
                    试拣
                  </Button>
                  <Button
                    size="small"
                    onClick={onPrintRightTable}
                    disabled={isRightEmpty}
                    style={{
                      background: isRightEmpty ? '#d9d9d9' : '#faad14',
                      borderColor: isRightEmpty ? '#d9d9d9' : '#faad14',
                      color: '#ffffff',
                      marginLeft: 8,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 24,
                      lineHeight: '24px',
                      padding: '0 12px',
                      alignSelf: 'center'
                    }}
                  >
                    打印
                  </Button>
                  <Button
                    size="small"
                    onClick={onPickAllContainers}
                    disabled={!canPickAllContainers || isRightEmpty}
                    style={{
                      background: (!canPickAllContainers || isRightEmpty) ? '#d9d9d9' : '#52c41a',
                      borderColor: (!canPickAllContainers || isRightEmpty) ? '#d9d9d9' : '#52c41a',
                      color: '#ffffff',
                      marginLeft: 8,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 24,
                      lineHeight: '24px',
                      padding: '0 12px',
                      alignSelf: 'center'
                    }}
                  >
                    全部拣出
                  </Button>
                </div>
              )}
              <div style={{ color: '#595959' }}>{rightHeaderRight}</div>
            </div>
            {isReadonly ? (
              <div style={{ padding: 12, background:'#fafafa', border:'1px dashed #d9d9d9', borderRadius: 6, color:'#8c8c8c', lineHeight: 1.7 }}>
                本阶段已完成/已跳过：仓位表不再展示实时容器数据（避免与当时拣货快照不一致）。
                请使用标题行的“查看拣货快照”查看完成时的拣货结果。
              </div>
            ) : (
              <>
                <Table
                  size="small"
                  rowKey={(r:any) => `${String(r?.container_code ?? '')}|${String(r?.location_code ?? '')}`}
                  columns={rightColumns}
                  dataSource={displayRowsPaged}
                  pagination={false}
                  loading={backendLoading}
                  onRow={(record:any) => {
                    const remain = Number(record?.theoretical_remain_eff ?? record?.theoretical_remain ?? 0);
                    if (remain <= 0) {
                      return { style: { background: '#f0f0f0', ...(remain < 0 ? { fontWeight: 700, color: '#cf1322' } : {}) } };
                    }
                    return {};
                  }}
                  locale={{ emptyText: selectedLeftRows.length === 0 ? '请在左侧选择拣货目标' : '暂无数据' }}
                />
                <div style={{ display:'flex', justifyContent:'flex-end', marginTop: 8 }}>
                  <Pagination
                    size="small"
                    current={rightPage}
                    pageSize={rightPageSize}
                    total={displayRows.length}
                    showSizeChanger
                    showQuickJumper
                    onChange={(p, ps) => {
                      setRightPage(p);
                      setRightPageSize(ps);
                    }}
                    showTotal={(total) => `共 ${total} 条`}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div style={{ background:'#ffffff', border:'1px solid #f0f0f0', borderRadius: 6, padding: 8 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 8 }}>
          <div style={{ fontWeight: 600 }}>拣货操作记录</div>
          <div style={{ color:'#595959', display:'flex', alignItems:'center' }}>
            <span>已拣货数量：<b>{pickStats.totalQty}</b></span>
            <span style={vSepStyle} />
            <span>已拣货种类：<b>{pickStats.kinds}</b></span>
            <span style={vSepStyle} />
            <span>涉及容器：<b>{pickStats.containers}</b></span>
          </div>
        </div>
        <div style={{ display:'flex', gap: 12, flexWrap:'wrap', opacity: isReadonly ? 0.55 : 1, pointerEvents: isReadonly ? 'none' as any : 'auto', filter: isReadonly ? 'grayscale(0.15)' : undefined }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ width: 90 }}>扫描容器</span>
            <Input
              ref={containerInputRef}
              value={metrics?.container_code}
              onChange={(e)=> onChange('container_code', e.target.value)}
              onPressEnter={() => {
                if (manualMode) {
                  const val = String(metrics?.container_code || '').trim();
                  if (val) { judgeContainerByCode(val); }
                }
              }}
              placeholder="扫描/输入容器条码"
              style={{ width: 240 }}
              disabled={canOperate === false}
            />
            {canOperate === false ? <span style={{ color:'#8c8c8c' }}>当前阶段已完成或已跳过，无法操作</span> : null}
            <div style={{ display:'inline-flex', alignItems:'center', gap:8, marginLeft: 8 }}>
              <span style={{ color:'#595959' }}>手动输入</span>
              <Switch checked={manualMode} onChange={(v)=> setManualMode(!!v)} size="small" disabled={canOperate === false} />
              <Button
                type="primary"
                onClick={() => {
                  const val = String(metrics?.container_code || '').trim();
                  if (val) { judgeContainerByCode(val); }
                }}
                disabled={canOperate === false || !manualMode || !String(metrics?.container_code || '').trim()}
              >
                提交
              </Button>
            </div>
            <Button danger onClick={handleClearAllRecords} style={{ marginLeft: 12 }} disabled={canOperate === false}>删除全部记录</Button>
          </div>
        </div>
        {/* 拣货记录表格（仅UI占位） */}
        <div style={{ marginTop: 12 }}>
          <Table
            size="small"
            columns={[
              {
                title: '序号',
                key: 'idx',
                width: 70,
                align: 'center',
                render: (_: any, __: any, index: number) => index + 1
              },
              {
                title: '扫描容器',
                dataIndex: 'container_code',
                key: 'container_code',
                width: 160,
                render: (v: any) => (
                  <Typography.Text copyable={{ text: String(v ?? '') }} style={{ fontFamily: 'monospace', color: '#595959' }}>
                    {v || '-'}
                  </Typography.Text>
                )
              },
              {
                title: '目标物',
                dataIndex: 'target_code',
                key: 'target_code',
                width: 200,
                render: (v: any) => (
                  <Typography.Text copyable={{ text: String(v ?? '') }} style={{ fontFamily: 'monospace', color: '#595959' }}>
                    {v || '-'}
                  </Typography.Text>
                )
              },
              {
                title: '目标物标识',
                dataIndex: 'sorting_mark',
                key: 'sorting_mark',
                width: 220,
                ellipsis: true,
                render: (v: any) => v || '-'
              },
              {
                title: '数量',
                dataIndex: 'qty',
                key: 'qty',
                width: 100,
                align: 'right',
                render: (v: any) => (typeof v === 'number' ? v : '-')
              },
              {
                title: '容器已下架',
                dataIndex: 'off_shelf',
                key: 'off_shelf',
                width: 110,
                align: 'center',
                render: (_: any, record: any) => (
                  <Checkbox
                    checked={!!record?.off_shelf}
                    disabled={canOperate === false}
                    onChange={(e) => {
                      const checked = !!e.target.checked;
                      const code = String(record?.container_code || '').trim();
                      setPickLogs(prev => prev.map(r => {
                        const sameContainer = String(r?.container_code || '').trim() === code;
                        return sameContainer ? { ...r, off_shelf: checked } : r;
                      }));
                    }}
                  />
                )
              },
              {
                title: '操作时间',
                dataIndex: 'operated_at',
                key: 'operated_at',
                width: 180,
                render: (v: any) => v || '-'
              },
              {
                title: '操作',
                key: 'actions',
                width: 160,
                align: 'center',
                render: (_:any, record:any) => (
                  <Space size="small">
                    <Button type="link" onClick={()=> openEdit(record)} disabled={canOperate === false}>修改</Button>
                    <Button type="link" danger onClick={()=> removeRecord(record)} disabled={canOperate === false}>删除</Button>
                  </Space>
                )
              }
            ] as any}
            dataSource={pickLogs as any}
            rowKey="id"
            pagination={false}
            bordered
            locale={{ emptyText: '暂无拣货记录' }}
          />
        </div>
      </div>

      {/* 容器内容明细弹窗 */}
      <Modal
        title={`容器内容明细 - ${contentsModalContainer || ''}`}
        open={contentsModalVisible}
        onCancel={() => setContentsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setContentsModalVisible(false)}>关闭</Button>
        ]}
        width={720}
      >
        {(() => {
          const items = contentsSummaryMap[contentsModalContainer] || [];
          if (!items || items.length === 0) {
            return <div style={{ color: '#999' }}>{contentsLoading ? '加载中...' : '无内容'}</div>;
          }
          return (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'left' }}>库存单元</th>
                  <th style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'left' }}>分拣标识</th>
                  <th style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'right' }}>数量</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it: any, idx: number) => {
                  const u = String(it?.unit_code || '').trim();
                  const q = Number(it?.qty || 0);
                  let mark = '-';
                  try {
                    if (Array.isArray(it?.sorting_codes) && it.sorting_codes.length > 0) {
                      mark = it.sorting_codes.filter(Boolean).join(' / ');
                    } else if (it?.sorting_code) {
                      mark = String(it.sorting_code);
                    }
                  } catch {}
                  return (
                    <tr key={`${u}|${idx}`}>
                      <td style={{ border: '1px solid #d9d9d9', padding: '6px 8px', fontFamily: 'monospace', color: '#595959' }}>{u}</td>
                      <td style={{ border: '1px solid #d9d9d9', padding: '6px 8px' }}>{mark}</td>
                      <td style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'right' }}>{q}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          );
        })()}
      </Modal>

      {/* 容器内容与已拣出合并明细弹窗 */}
      <Modal
        title={`容器内容与已拣出明细 - ${inOutModalContainer || ''}`}
        open={inOutModalVisible}
        onCancel={() => setInOutModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setInOutModalVisible(false)}>关闭</Button>
        ]}
        width={780}
      >
        {(() => {
          const code = inOutModalContainer;
          const items = contentsSummaryMap[code] || [];
          if (!items || items.length === 0) {
            return <div style={{ color: '#999' }}>{contentsLoading ? '加载中...' : '无内容'}</div>;
          }
          // 基于拣货记录实时汇总该容器每个目标物的拣出数量
          const unitPickedMap: Record<string, number> = {};
          (pickLogs || [])
            .filter(r => String(r?.container_code || '').trim() === code)
            .forEach(r => {
              const u = String(r?.target_code || '').trim();
              const q = Number(r?.qty || 0);
              if (!u || !(q > 0)) return;
              unitPickedMap[u] = (unitPickedMap[u] || 0) + q;
            });
          const rows = items.map((it:any) => {
            const unit = String(it?.unit_code || '').trim();
            const qty = Number(it?.qty || 0);
            const picked = Number(unitPickedMap[unit] || 0);
            let mark = '-';
            try {
              if (Array.isArray(it?.sorting_codes) && it.sorting_codes.length > 0) {
                mark = it.sorting_codes.filter(Boolean).join(' / ');
              } else if (it?.sorting_code) {
                mark = String(it.sorting_code);
              }
            } catch {}
            return { unit, mark, qty, picked };
          });
          return (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'left' }}>库存单元</th>
                  <th style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'left' }}>分拣标识</th>
                  <th style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'right' }}>容器内数量</th>
                  <th style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'right' }}>已拣出</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={`${r.unit}|inout|${idx}`}>
                    <td style={{ border: '1px solid #d9d9d9', padding: '6px 8px', fontFamily: 'monospace', color: '#595959' }}>{r.unit}</td>
                    <td style={{ border: '1px solid #d9d9d9', padding: '6px 8px' }}>{r.mark}</td>
                    <td style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'right' }}>{r.qty}</td>
                    <td style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'right' }}>{r.picked}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          );
        })()}
      </Modal>

      {/* 已拣出明细弹窗（UI占位） */}
      <Modal
        title={`拣出明细 - ${pickedModalContainer || ''}`}
        open={pickedModalVisible}
        onCancel={() => setPickedModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPickedModalVisible(false)}>关闭</Button>
        ]}
        width={720}
      >
        {(() => {
          const code = pickedModalContainer;
          const items = contentsSummaryMap[code] || [];
          if (!items || items.length === 0) {
            return <div style={{ color: '#999' }}>{contentsLoading ? '加载中...' : '无内容'}</div>;
          }
          // 基于拣货记录实时汇总该容器每个目标物的拣出数量（精确映射，不做启发式分摊）
          const unitPickedMap: Record<string, number> = {};
          (pickLogs || [])
            .filter(r => String(r?.container_code || '').trim() === code)
            .forEach(r => {
              const u = String(r?.target_code || '').trim();
              const q = Number(r?.qty || 0);
              if (!u || !(q > 0)) return;
              unitPickedMap[u] = (unitPickedMap[u] || 0) + q;
            });
          const rows = items.map((it:any) => {
            const unit = String(it?.unit_code || '').trim();
            const picked = Number(unitPickedMap[unit] || 0);
            let mark = '-';
            try {
              if (Array.isArray(it?.sorting_codes) && it.sorting_codes.length > 0) {
                mark = it.sorting_codes.filter(Boolean).join(' / ');
              } else if (it?.sorting_code) {
                mark = String(it.sorting_code);
              }
            } catch {}
            return { unit, mark, picked };
          });
          const totalPicked = rows.reduce((s, r)=> s + r.picked, 0);
          return (
            <div>
              <div style={{ marginBottom: 8, color: '#595959' }}>
                拣出总计：{totalPicked}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'left' }}>库存单元</th>
                    <th style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'left' }}>分拣标识</th>
                    <th style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'right' }}>拣出数量</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => (
                    <tr key={`${r.unit}|picked|${idx}`}>
                      <td style={{ border: '1px solid #d9d9d9', padding: '6px 8px', fontFamily: 'monospace', color: '#595959' }}>{r.unit}</td>
                      <td style={{ border: '1px solid #d9d9d9', padding: '6px 8px' }}>{r.mark}</td>
                      <td style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'right' }}>{r.picked}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2} style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>合计</td>
                    <td style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>{totalPicked}</td>
                  </tr>
                </tfoot>
              </table>
              <div style={{ marginTop: 8, color: '#999' }}>注：当前为UI演示，分配逻辑待与业务口径对齐后完善。</div>
            </div>
          );
        })()}
      </Modal>

      {/* 编辑拣货记录弹窗（仅允许修改数量） */}
      <Modal
        title="修改拣货记录"
        open={editVisible}
        onCancel={()=> setEditVisible(false)}
        onOk={submitEdit}
        okText="保存"
        cancelText="取消"
      >
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ width: 100, color:'#595959' }}>扫描容器</span>
            <Input value={editForm.container_code} disabled style={{ width: 260 }} />
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ width: 100, color:'#595959' }}>目标物</span>
            <Input value={editForm.target_code} disabled style={{ width: 260 }} />
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ width: 100, color:'#595959' }}>拣货数量</span>
            <InputNumber
              min={1}
              value={editForm.qty}
              onChange={(nv)=> setEditForm(f => ({ ...f, qty: Number(nv || 0) }))}
              style={{ width: 260 }}
            />
          </div>
        </div>
      </Modal>

      {/* 不可全部拣出（部分目标物超量） - 扫描目标物并记录“已拣出” */}
      <Modal
        title={(
          <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-start', gap: 12, width:'100%' }}>
            <span>不可全部拣出（部分目标物超量）</span>
            <span style={{ color:'#595959' }}>容器：{overPickContainer || '-'}</span>
          </div>
        )}
        open={overPickVisible}
        onCancel={() => setOverPickVisible(false)}
        footer={[
          <Checkbox
            key="off_shelf"
            checked={overPickOffShelf}
            onChange={(e)=> setOverPickOffShelf(!!e.target.checked)}
          >
            容器已下架
          </Checkbox>,
          <Button key="cancel" onClick={() => setOverPickVisible(false)}>取消</Button>,
          <Button
            key="submit"
            type="primary"
            onClick={() => {
              const container = String(overPickContainer || '').trim();
              const now = new Date();
              const logs = (overPickRows || [])
                .filter(r => Number(r.picked || 0) > 0)
                .map(r => ({
                  id: `${now.getTime()}_${r.unit}_${Math.floor(Math.random()*100000)}`,
                  container_code: container,
                  target_code: String(r.unit || '').trim(),
                  sorting_mark: String(r.mark || ''),
                  qty: Number(r.picked || 0),
                  operated_at: now.toLocaleString(),
                  off_shelf: !!overPickOffShelf,
                }));
              if (logs.length > 0) {
                setPickLogs(prev => {
                  const synced = prev.map(rec => {
                    const same = String(rec?.container_code || '').trim() === container;
                    return same ? { ...rec, off_shelf: !!overPickOffShelf } : rec;
                  });
                  return [...logs, ...synced];
                });
                message.success(`已加入拣货记录：${logs.length} 条`);
              } else {
                message.info('没有需要提交的“已拣出”记录');
              }
              setOverPickVisible(false);
            }}
          >
            提交
          </Button>
        ]}
        width={980}
      >
        <div style={{ marginBottom: 8, color: '#a8071a' }}>
          该容器部分目标物超过拣货差额，不能整容器全部拣出；请逐一扫描或手动输入“已拣出”。
        </div>
        {/* 扫描输入（单独一行，位于提示信息下方） */}
        <div style={{ display:'flex', alignItems:'center', gap:8, margin: '0 0 8px 0' }}>
          <span style={{ color:'#595959' }}>扫描目标物</span>
          <Input
            ref={overScanRef}
            value={overPickScan}
            onChange={(e)=> setOverPickScan(e.target.value)}
            onPressEnter={submitOverScan}
            placeholder="扫描/输入目标物条码后回车"
            style={{ width: 320 }}
          />
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid #d9d9d9', padding: 6, textAlign: 'left' }}>目标物</th>
              <th style={{ border: '1px solid #d9d9d9', padding: 6, textAlign: 'left' }}>分拣标识</th>
              <th style={{ border: '1px solid #d9d9d9', padding: 6, textAlign: 'right' }}>容器内数量</th>
              <th style={{ border: '1px solid #d9d9d9', padding: 6, textAlign: 'right' }}>拣货差额</th>
              <th style={{ border: '1px solid #d9d9d9', padding: 6, textAlign: 'right' }}>已拣出</th>
            </tr>
          </thead>
          <tbody>
            {overPickRows.map((r, i) => {
              const needNum = Number(r.need || 0);
              const pickedNum = Number(r.picked || 0);
              const haveNum = Number(r.have || 0);
              // 完成优先：need<=0（差额为0或负）或已拣出==差额 → 绿色背景
              const done = (needNum <= 0) || (pickedNum === needNum);
              // 未完成时才判断“超额”高亮：容器内数量>差额 或 已拣出>差额
              const isOver = (haveNum > needNum || pickedNum > needNum);
              // 组合样式：绿色背景（done）与红色加粗（isOver）互不覆盖
              const rowStyle: React.CSSProperties = {
                ...(done ? { background: '#f6ffed' } : {}),
                ...(isOver ? { color: '#cf1322', fontWeight: 700 } : {}),
              };
              return (
                <tr key={`${r.unit}|overpick|${i}`} style={rowStyle}>
                  <td style={{ border: '1px solid #d9d9d9', padding: 6, fontFamily: 'monospace' }}>{r.unit}</td>
                  <td style={{ border: '1px solid #d9d9d9', padding: 6 }}>{r.mark || '-'}</td>
                  <td style={{ border: '1px solid #d9d9d9', padding: 6, textAlign: 'right' }}>{r.have}</td>
                  <td style={{ border: '1px solid #d9d9d9', padding: 6, textAlign: 'right' }}>{r.need}</td>
                  <td style={{ border: '1px solid #d9d9d9', padding: 6, textAlign: 'right' }}>
                    <InputNumber
                      min={0}
                      value={r.picked || 0}
                      onChange={(nv)=> {
                        const v = Number(nv || 0);
                        setOverPickRows(prev => {
                          const next = [...prev];
                          next[i] = { ...next[i], picked: v };
                          return next;
                        });
                      }}
                      style={{ width: 120 }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Modal>

      {/* 不可全部拣出（包含非目标物） - 扫描目标物并记录“已拣出” */}
      <Modal
        title={(
          <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-start', gap: 12, width:'100%' }}>
            <span>不可全部拣出（包含非目标物）</span>
            <span style={{ color:'#595959' }}>容器：{ntPickContainer || '-'}</span>
          </div>
        )}
        open={ntPickVisible}
        onCancel={() => setNtPickVisible(false)}
        footer={[
          <Checkbox
            key="off_shelf"
            checked={ntPickOffShelf}
            onChange={(e)=> setNtPickOffShelf(!!e.target.checked)}
          >
            容器已下架
          </Checkbox>,
          <Button key="cancel" onClick={() => setNtPickVisible(false)}>取消</Button>,
          <Button
            key="submit"
            type="primary"
            onClick={() => {
              const container = String(ntPickContainer || '').trim();
              const now = new Date();
              const logs = (ntPickRows || [])
                .filter(r => Number(r.picked || 0) > 0)
                .map(r => ({
                  id: `${now.getTime()}_${r.unit}_${Math.floor(Math.random()*100000)}`,
                  container_code: container,
                  target_code: String(r.unit || '').trim(),
                  sorting_mark: String(r.mark || ''),
                  qty: Number(r.picked || 0),
                  operated_at: now.toLocaleString(),
                  off_shelf: !!ntPickOffShelf,
                }));
              if (logs.length > 0) {
                setPickLogs(prev => {
                  const synced = prev.map(rec => {
                    const same = String(rec?.container_code || '').trim() === container;
                    return same ? { ...rec, off_shelf: !!ntPickOffShelf } : rec;
                  });
                  return [...logs, ...synced];
                });
                message.success(`已加入拣货记录：${logs.length} 条`);
              } else {
                message.info('没有需要提交的“已拣出”记录');
              }
              setNtPickVisible(false);
            }}
          >
            提交
          </Button>
        ]}
        width={980}
      >
        <div style={{ marginBottom: 8, color: '#a8071a' }}>
          该容器包含非目标物，不能整容器全部拣出；请逐一扫描或手动输入“已拣出”。
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, margin: '0 0 8px 0' }}>
          <span style={{ color:'#595959' }}>扫描目标物</span>
          <Input
            ref={ntScanRef}
            value={ntPickScan}
            onChange={(e)=> setNtPickScan(e.target.value)}
            onPressEnter={submitNtScan}
            placeholder="扫描/输入目标物条码后回车"
            style={{ width: 320 }}
          />
        </div>
        {(() => {
          const targets = (ntPickRows || []).filter(r => !r.isNonTarget);
          const nonTargets = (ntPickRows || []).filter(r => !!r.isNonTarget);
          return (
            <div>
              {/* 目标物列表 */}
              {targets.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ border: '1px solid #d9d9d9', padding: 6, textAlign: 'left', width: 220 }}>目标物</th>
                        <th style={{ border: '1px solid #d9d9d9', padding: 6, textAlign: 'left', width: 220 }}>分拣标识</th>
                        <th style={{ border: '1px solid #d9d9d9', padding: 6, textAlign: 'right', width: 120 }}>容器内数量</th>
                        <th style={{ border: '1px solid #d9d9d9', padding: 6, textAlign: 'right', width: 120 }}>拣货差额</th>
                        <th style={{ border: '1px solid #d9d9d9', padding: 6, textAlign: 'right', width: 140 }}>已拣出</th>
                      </tr>
                    </thead>
                    <tbody>
                      {targets.map((r, idx) => {
                        const equal = Number(r.picked || 0) === Number(r.need || 0);
                        const over  = Number(r.picked || 0) > Number(r.need || 0);
                        const rowStyle: React.CSSProperties = equal
                          ? { background: '#f6ffed' }
                          : (over ? { color: '#cf1322', fontWeight: 700 } : {});
                        return (
                          <tr key={`${r.unit}|ntpick-t|${idx}`} style={rowStyle}>
                            <td style={{ border: '1px solid #d9d9d9', padding: 6, fontFamily: 'monospace', color: over ? '#cf1322' : '#595959', fontWeight: over ? 700 : undefined }}>{r.unit}</td>
                            <td style={{ border: '1px solid #d9d9d9', padding: 6 }}>{r.mark || '-'}</td>
                            <td style={{ border: '1px solid #d9d9d9', padding: 6, textAlign: 'right' }}>{r.have}</td>
                            <td style={{ border: '1px solid #d9d9d9', padding: 6, textAlign: 'right' }}>{r.need}</td>
                            <td style={{ border: '1px solid #d9d9d9', padding: 6, textAlign: 'right' }}>
                              <InputNumber
                                min={0}
                                value={r.picked || 0}
                                onChange={(nv)=> {
                                  const v = Number(nv || 0);
                                  setNtPickRows(prev => {
                                    const next = [...prev];
                                    const i = next.findIndex(x => x.unit === r.unit && !!x.isNonTarget === !!r.isNonTarget);
                                    if (i >= 0 && !next[i].isNonTarget) {
                                      next[i] = { ...next[i], picked: v };
                                    }
                                    return next;
                                  });
                                }}
                                style={{ width: 120 }}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {/* 非目标物列表 */}
              {nonTargets.length > 0 && (
                <div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ border: '1px solid #d9d9d9', padding: 6, textAlign: 'left', width: 220 }}>非目标物</th>
                        <th style={{ border: '1px solid #d9d9d9', padding: 6, textAlign: 'left', width: 220 }}>分拣标识</th>
                        <th style={{ border: '1px solid #d9d9d9', padding: 6, textAlign: 'right', width: 120 }}>容器内数量</th>
                        <th style={{ border: '1px solid #d9d9d9', padding: 6, textAlign: 'right', width: 120 }}>拣货差额</th>
                        <th style={{ border: '1px solid #d9d9d9', padding: 6, textAlign: 'right', width: 140 }}>已拣出</th>
                      </tr>
                    </thead>
                    <tbody>
                      {nonTargets.map((r, idx) => {
                        const rowStyle: React.CSSProperties = { background: '#f0f0f0' };
                        return (
                          <tr key={`${r.unit}|ntpick-nt|${idx}`} style={rowStyle}>
                            <td style={{ border: '1px solid #d9d9d9', padding: 6, fontFamily: 'monospace', color: '#595959' }}>{r.unit}</td>
                            <td style={{ border: '1px solid #d9d9d9', padding: 6 }}>{r.mark || '-'}</td>
                            <td style={{ border: '1px solid #d9d9d9', padding: 6, textAlign: 'right' }}>{r.have}</td>
                            <td style={{ border: '1px solid #d9d9d9', padding: 6, textAlign: 'right' }}>{r.need}</td>
                            <td style={{ border: '1px solid #d9d9d9', padding: 6, textAlign: 'right' }}>
                              <InputNumber
                                min={0}
                                value={r.picked || 0}
                                disabled
                                style={{ width: 120 }}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })()}
      </Modal>
      {/* 理论余量明细弹窗 */}
      <Modal
        title={`理论余量明细 - ${remainModalContainer || ''}`}
        open={remainModalVisible}
        onCancel={() => setRemainModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setRemainModalVisible(false)}>关闭</Button>
        ]}
        width={760}
      >
        {(() => {
          const code = remainModalContainer;
          const items = contentsSummaryMap[code] || [];
          if (!items || items.length === 0) {
            return <div style={{ color: '#999' }}>{contentsLoading ? '加载中...' : '无内容'}</div>;
          }
          // 基于拣货记录实时汇总该容器每个目标物的拣出数量（精确映射，不做启发式分摊）
          const unitPickedMap: Record<string, number> = {};
          (pickLogs || [])
            .filter(r => String(r?.container_code || '').trim() === code)
            .forEach(r => {
              const u = String(r?.target_code || '').trim();
              const q = Number(r?.qty || 0);
              if (!u || !(q > 0)) return;
              unitPickedMap[u] = (unitPickedMap[u] || 0) + q;
            });
          const rows = items.map((it: any) => {
            const unit = String(it?.unit_code || '').trim();
            const qty = Number(it?.qty || 0);
            const pickedForThis = Number(unitPickedMap[unit] || 0);
            // 理论余量允许为负
            const remain = qty - pickedForThis;
            let mark = '-';
            try {
              if (Array.isArray(it?.sorting_codes) && it.sorting_codes.length > 0) {
                mark = it.sorting_codes.filter(Boolean).join(' / ');
              } else if (it?.sorting_code) {
                mark = String(it.sorting_code);
              }
            } catch {}
            return { unit, mark, qty, picked: pickedForThis, remain };
          });
          const totalQty = rows.reduce((s, r)=> s + r.qty, 0);
          const totalPicked = rows.reduce((s, r)=> s + r.picked, 0);
          const totalRemain = rows.reduce((s, r)=> s + r.remain, 0);
          return (
            <div>
              <div style={{ marginBottom: 8, color: '#595959' }}>
                容器内数量：{totalQty} ｜ 已拣出：{totalPicked} ｜ 理论余量：{totalRemain}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'left' }}>库存单元</th>
                    <th style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'left' }}>分拣标识</th>
                    <th style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'right' }}>容器内数量</th>
                    <th style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'right' }}>分摊已拣出</th>
                    <th style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'right' }}>理论余量</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => {
                    const styleNeg = r.remain < 0 ? { fontWeight: 700, color: '#cf1322' } : undefined;
                    return (
                      <tr key={`${r.unit}|${idx}`}>
                        <td style={{ border: '1px solid #d9d9d9', padding: '6px 8px', fontFamily: 'monospace', color: '#595959' }}>{r.unit}</td>
                        <td style={{ border: '1px solid #d9d9d9', padding: '6px 8px' }}>{r.mark}</td>
                        <td style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'right' }}>{r.qty}</td>
                        <td style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'right' }}>{r.picked}</td>
                        <td style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'right', ...(styleNeg || {}) }}>{r.remain}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2} style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>合计</td>
                    <td style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>{totalQty}</td>
                    <td style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>{totalPicked}</td>
                    <td style={{ border: '1px solid #d9d9d9', padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>{totalRemain}</td>
                  </tr>
                </tfoot>
              </table>
              <div style={{ marginTop: 8, color: '#999' }}>
                注：分摊“已拣出”仅对当前已选目标物进行顺序分配，用于估算理论余量。
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
};

export default PickStepRenderer;


